<template>
  <div class="dashboard-container">
    <b-modal id="customer-select-modal" hide-footer :hide-header-close="true" size="xl" no-close-on-backdrop
      no-close-on-esc centered title="Chọn khách hàng để bắt đầu">
      <b-container fluid>
        <PatientSelectList @select="handlePatientSelect" />
        <div class="mt-3 text-right">
          <b-button class="btn-close" @click="$bvModal.hide('customer-select-modal')" type="button">
            Đóng
          </b-button>
        </div>
      </b-container>
    </b-modal>
    <!-- Main Content -->
    <div class="main-wrapper">
      <div class="patient-info-card">
        <div class="patient-info-header">
          <div class="patient-badge">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="#10b981">
              <circle cx="8" cy="8" r="8" fill="currentColor" />
              <path d="M11 6L7 10L5 8" stroke="white" stroke-width="2" fill="none" stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
            <span>Thông tin bệnh nhân</span>
          </div>
          <div>
            <span class="last-updated">Dữ liệu cập nhật lần cuối: {{ formatDateTime(Date.now()) }}</span>

            <b-button class="btn-close" @click="handleRefresh()" :disabled="!userInformation.patientId || isRefreshing">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14"
                  stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                <path d="M14 8L12 6M14 8L12 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
              {{ isRefreshing ? 'Đang làm mới...' : 'Làm mới' }}
            </b-button>
            <b-button class="patient-actions" @click="$bvModal.show('customer-select-modal')">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="7" cy="7" r="5" stroke="currentColor" stroke-width="1.5" />
                <path d="M11 11L14 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
              </svg>
              Tìm kiếm
            </b-button>

          </div>

        </div>

        <div class="patient-meta-info">
          <strong class="patient-name-lg">{{ userInformation.name || "Khách hàng" }}</strong>
          <span class="patient-basic-info">{{ userInformation.gender }} / {{ userInformation.age }} tuổi</span>
          <span class="patient-meta">{{ userInformation.typeOfSick || "Tiền đái tháo đường" }}</span>
          <!-- Tag Badge -->
          <span v-if="userInformation.tag" class="patient-tag-badges">
            <span v-if="userInformation.tag.toLowerCase() === 'diab'" class="tag-badge tag-diab">DIAB</span>
            <span v-else-if="userInformation.tag.toLowerCase() === 'his'" class="tag-badge tag-his">HIS</span>
            <span v-else-if="userInformation.tag.toLowerCase() === 'both'" class="tag-badges-group">
              <span class="tag-badge tag-diab">DIAB</span>
              <span class="tag-badge tag-his">HIS</span>
            </span>
          </span>
        </div>
      </div>

      <!-- Tab Navigation -->
      <nav class="tab-navigation">
        <button :class="['tab-button', { active: activeTab === 'overview' }]" @click="switchTab('overview')">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          </svg>
          Chỉ số tổng quát
        </button>
        <button
          :class="['tab-button', { active: activeTab === 'communication', disabled: !canAccessCommunicationTab }]"
          @click="canAccessCommunicationTab && switchTab('communication')"
          :disabled="!canAccessCommunicationTab"
          v-b-tooltip.hover="communicationTabLockedReason"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" stroke-width="1.5" />
            <path d="M2 6h12" stroke="currentColor" stroke-width="1.5" />
          </svg>
          Trao đổi / Liên hệ
          <span v-if="!canAccessCommunicationTab" class="tab-lock-icon">🔒</span>
        </button>
        <button :class="['tab-button', { active: activeTab === 'rest' }]" @click="switchTab('rest')">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 2v12M2 8h12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          </svg>
          Khuyến nghị
        </button>
        <button :class="['tab-button', { active: activeTab === 'profile' }]" @click="switchTab('profile')">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.5" />
            <path d="M8 5v3l2 2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          </svg>
          Hồ sơ bệnh nhân
        </button>
      </nav>

      <!-- Main Content Area -->
      <div class="content-wrapper">
        <!-- Tab Contents Wrapper -->
        <div class="tabs-container">

          <!-- Overview Tab Content -->
          <div v-if="activeTab === 'overview'" class="tab-content-section">

            <!-- Overview Section Title -->
            <div class="section-title-header">
              <h2>Chỉ số tổng quát</h2>
              <span class="last-updated">Lần khám tiếp theo: {{ lastestMetricsNextBloodPressure.nextExaminationDate ||
                'chưa có lịch' }}</span>
            </div>

            <!-- Health Metrics Cards -->
            <div class="chart-section">

              <div class="metrics-grid">
                <!-- Blood Sugar -->
                <div class="metric-card" :class="{ disabled: !latestMetrics.glucose }">
                  <div class="metric-header">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M10 2C10 2 5 6 5 10C5 12.7614 7.23858 15 10 15C12.7614 15 15 12.7614 15 10C15 6 10 2 10 2Z"
                        stroke="#ef4444" stroke-width="1.5" fill="none" />
                    </svg>
                    <span class="metric-label">Đường huyết</span>
                  </div>
                  <div class="metric-value">
                    <span class="value-number">{{ latestMetrics.glucose || '--' }}</span>
                    <span class="value-unit">{{ latestMetrics.glucoseUnit }}</span>
                    <!-- Indicator -->
                    <span v-if="latestMetrics.glucoseTrend?.direction"
                          :class="['metric-indicator', latestMetrics.glucoseTrend.direction]">
                      <svg v-if="latestMetrics.glucoseTrend.direction === 'up'" width="12" height="12" viewBox="0 0 12 12">
                        <path d="M6 2L10 7H2L6 2Z" fill="currentColor"/>
                      </svg>
                      <svg v-else-if="latestMetrics.glucoseTrend.direction === 'down'" width="12" height="12" viewBox="0 0 12 12">
                        <path d="M6 10L2 5H10L6 10Z" fill="currentColor"/>
                      </svg>
                      <span class="indicator-value">{{ latestMetrics.glucoseTrend.diff }}</span>
                    </span>
                  </div>
                  <span :class="['metric-status', getGlucoseStatus(latestMetrics.glucoseType)]">
                    {{ latestMetrics.glucoseType }}
                  </span>
                  <div class="metric-date" v-if="latestMetrics.glucoseDate">
                    {{ formatDate(latestMetrics.glucoseDate) }}
                  </div>
                </div>

                <!-- Blood Pressure -->
                <div class="metric-card" :class="{ disabled: !latestMetrics.systolic }">
                  <div class="metric-header">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M3 10h4l2 4 2-8 2 4h4" stroke="#10b981" stroke-width="1.5" fill="none"
                        stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <span class="metric-label">Huyết áp</span>
                  </div>
                  <div class="metric-value">
                    <span class="value-number">{{ latestMetrics.systolic || '--' }}/{{ latestMetrics.diastolic || '--'
                    }}</span>
                    <span class="value-unit">{{ latestMetrics.bpUnit }}</span>
                    <!-- Indicator -->
                    <span v-if="latestMetrics.bloodPressureTrend?.direction"
                          :class="['metric-indicator', latestMetrics.bloodPressureTrend.direction]">
                      <svg v-if="latestMetrics.bloodPressureTrend.direction === 'up'" width="12" height="12" viewBox="0 0 12 12">
                        <path d="M6 2L10 7H2L6 2Z" fill="currentColor"/>
                      </svg>
                      <svg v-else-if="latestMetrics.bloodPressureTrend.direction === 'down'" width="12" height="12" viewBox="0 0 12 12">
                        <path d="M6 10L2 5H10L6 10Z" fill="currentColor"/>
                      </svg>
                      <span class="indicator-value">{{ latestMetrics.bloodPressureTrend.diff }}</span>
                    </span>
                  </div>
                  <span :class="['metric-status', getBloodPressureStatus(latestMetrics.bloodPressureType)]">
                    {{ latestMetrics.bloodPressureType }}
                  </span>
                  <div class="metric-date" v-if="latestMetrics.bloodPressureDate">
                    {{ formatDate(latestMetrics.bloodPressureDate) }}
                  </div>
                </div>

                <!-- Weight -->
                <div class="metric-card" :class="{ disabled: !latestMetrics.weight }">
                  <div class="metric-header">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M10 2v16M7 5l3-3 3 3M7 15l3 3 3-3" stroke="#06b6d4" stroke-width="1.5"
                        stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <span class="metric-label">Cân nặng</span>
                  </div>
                  <div class="metric-value">
                    <span class="value-number">{{ latestMetrics.weight || '--' }}</span>
                    <span class="value-unit">{{ latestMetrics.weightUnit }}</span>
                    <!-- Indicator -->
                    <span v-if="latestMetrics.weightTrend?.direction"
                          :class="['metric-indicator', latestMetrics.weightTrend.direction]">
                      <svg v-if="latestMetrics.weightTrend.direction === 'up'" width="12" height="12" viewBox="0 0 12 12">
                        <path d="M6 2L10 7H2L6 2Z" fill="currentColor"/>
                      </svg>
                      <svg v-else-if="latestMetrics.weightTrend.direction === 'down'" width="12" height="12" viewBox="0 0 12 12">
                        <path d="M6 10L2 5H10L6 10Z" fill="currentColor"/>
                      </svg>
                      <span class="indicator-value">{{ latestMetrics.weightTrend.diff }}</span>
                    </span>
                  </div>
                  <span :class="['metric-status', getWeightStatus(latestMetrics.bmiType)]">
                    {{ latestMetrics.bmiType }}
                  </span>
                  <div class="metric-date" v-if="latestMetrics.weightDate">
                    {{ formatDate(latestMetrics.weightDate) }}
                  </div>
                </div>

                <!-- Waist -->
                <div class="metric-card" :class="{ disabled: !latestMetrics.waist }">
                  <div class="metric-header">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <rect x="3" y="8" width="14" height="8" rx="1" stroke="#8b5cf6" stroke-width="1.5" />
                      <path d="M7 8V6a3 3 0 016 0v2" stroke="#8b5cf6" stroke-width="1.5" stroke-linecap="round" />
                    </svg>
                    <span class="metric-label">Vòng eo</span>
                  </div>
                  <div class="metric-value">
                    <span class="value-number">{{ latestMetrics.waist || '--' }}</span>
                    <span class="value-unit">{{ latestMetrics.waistUnit }}</span>
                    <!-- Indicator -->
                    <span v-if="latestMetrics.waistTrend?.direction"
                          :class="['metric-indicator', latestMetrics.waistTrend.direction]">
                      <svg v-if="latestMetrics.waistTrend.direction === 'up'" width="12" height="12" viewBox="0 0 12 12">
                        <path d="M6 2L10 7H2L6 2Z" fill="currentColor"/>
                      </svg>
                      <svg v-else-if="latestMetrics.waistTrend.direction === 'down'" width="12" height="12" viewBox="0 0 12 12">
                        <path d="M6 10L2 5H10L6 10Z" fill="currentColor"/>
                      </svg>
                      <span class="indicator-value">{{ latestMetrics.waistTrend.diff }}</span>
                    </span>
                  </div>
                  <span class="metric-status success">
                    {{ latestMetrics.waist ? 'Đã cập nhật' : 'Chưa có dữ liệu' }}
                  </span>
                  <div class="metric-date" v-if="latestMetrics.waistDate">
                    {{ formatDate(latestMetrics.waistDate) }}
                  </div>
                </div>

                <!-- HbA1C -->
                <div class="metric-card" :class="{ disabled: !latestMetrics.hba1c }">
                  <div class="metric-header">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <circle cx="10" cy="10" r="7" stroke="#f59e0b" stroke-width="1.5" />
                      <path d="M10 6v4l3 3" stroke="#f59e0b" stroke-width="1.5" stroke-linecap="round" />
                    </svg>
                    <span class="metric-label">HbA1C</span>
                  </div>
                  <div class="metric-value">
                    <span class="value-number">{{ latestMetrics.hba1c || '--' }}</span>
                    <span class="value-unit">{{ latestMetrics.hba1cUnit }}</span>
                    <!-- Indicator -->
                    <span v-if="latestMetrics.hba1cTrend?.direction"
                          :class="['metric-indicator', latestMetrics.hba1cTrend.direction]">
                      <svg v-if="latestMetrics.hba1cTrend.direction === 'up'" width="12" height="12" viewBox="0 0 12 12">
                        <path d="M6 2L10 7H2L6 2Z" fill="currentColor"/>
                      </svg>
                      <svg v-else-if="latestMetrics.hba1cTrend.direction === 'down'" width="12" height="12" viewBox="0 0 12 12">
                        <path d="M6 10L2 5H10L6 10Z" fill="currentColor"/>
                      </svg>
                      <span class="indicator-value">{{ latestMetrics.hba1cTrend.diff }}</span>
                    </span>
                  </div>
                  <span :class="['metric-status', getHbA1cStatus(latestMetrics.hba1cType)]">
                    {{ latestMetrics.hba1cType }}
                  </span>
                  <div class="metric-date" v-if="latestMetrics.hba1cDate">
                    {{ formatDate(latestMetrics.hba1cDate) }}
                  </div>
                </div>

                <!-- BMI
                <div class="metric-card" :class="{ loading: !latestMetrics.bmi }">
                  <div class="metric-header">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M4 10h12M10 4v12" stroke="#06b6d4" stroke-width="1.5" stroke-linecap="round" />
                      <circle cx="10" cy="10" r="7" stroke="#06b6d4" stroke-width="1.5" />
                    </svg>
                    <span class="metric-label">BMI</span>
                  </div>
                  <div class="metric-value">
                    <span class="value-number">{{ latestMetrics.bmi || '--' }}</span>
                    <span class="value-unit">{{ latestMetrics.bmiUnit }}</span>
                  </div>
                  <span :class="['metric-status', getBmiStatus(latestMetrics.bmi)]">
                    {{ getBmiStatusText(latestMetrics.bmi) }}
                  </span>
                </div> -->

                <!-- Liver (Gan) -->
                <div class="metric-card" :class="{ disabled: !latestMetrics.liver }">
                  <div class="metric-header">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M6 8C6 8 8 6 10 6C12 6 14 8 14 8C14 8 15 9 15 11C15 13 13 15 10 15C7 15 5 13 5 11C5 9 6 8 6 8Z"
                        stroke="#dc2626" stroke-width="1.5" fill="none" />
                      <path d="M10 6V4M10 15V17" stroke="#dc2626" stroke-width="1.5" stroke-linecap="round" />
                    </svg>
                    <span class="metric-label">Gan (ALT)</span>
                  </div>
                  <div class="metric-value">
                    <span class="value-number">{{ latestMetrics.liver || '--' }}</span>
                    <span class="value-unit">{{ latestMetrics.liverUnit }}</span>
                  </div>
                  <span :class="['metric-status', getLiverStatus(latestMetrics.liver)]">
                    {{ getLiverStatusText(latestMetrics.liver) }}
                  </span>
                  <div class="metric-date" v-if="latestMetrics.liverDate">
                    {{ formatDate(latestMetrics.liverDate) }}
                  </div>
                </div>

                <!-- Kidney (Thận) -->
                <div class="metric-card" :class="{ disabled: !latestMetrics.kidney }">
                  <div class="metric-header">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <ellipse cx="10" cy="10" rx="4" ry="6" stroke="#7c3aed" stroke-width="1.5" fill="none" />
                      <path d="M10 6C10 6 12 7 12 10C12 13 10 14 10 14" stroke="#7c3aed" stroke-width="1.5"
                        stroke-linecap="round" />
                    </svg>
                    <span class="metric-label">Thận (Creatinine)</span>
                  </div>
                  <div class="metric-value">
                    <span class="value-number">{{ latestMetrics.kidney || '--' }}</span>
                    <span class="value-unit">{{ latestMetrics.kidneyUnit }}</span>
                  </div>
                  <span :class="['metric-status', getKidneyStatus(latestMetrics.kidney)]">
                    {{ getKidneyStatusText(latestMetrics.kidney) }}
                  </span>
                  <div class="metric-date" v-if="latestMetrics.kidneyDate">
                    {{ formatDate(latestMetrics.kidneyDate) }}
                  </div>
                </div>
              </div>

              <!-- Charts Section Title -->
              <div class="section-title-header mt-4">
                <h2>Biểu đồ tổng quát</h2>
              </div>

              <!-- Blood Sugar Chart -->
              <div class="chart-section">
                <div class="chart-header">
                  <div class="chart-title-group">
                    <h3>Đường huyết</h3>
                    <div class="chart-legend">
                      <span class="legend-item" :class="{ 'legend-hidden': isSeriesHidden('Đường huyết đói') }"
                        @click="toggleGlucoseSeries('Đường huyết đói')" style="cursor: pointer; user-select: none;">
                        <span class="legend-dot glucose-fasting"></span>
                        Đường huyết đói
                      </span>
                      <span class="legend-item" :class="{ 'legend-hidden': isSeriesHidden('Trước ăn') }"
                        @click="toggleGlucoseSeries('Trước ăn')" style="cursor: pointer; user-select: none;">
                        <span class="legend-dot before-meal"></span>
                        Trước ăn
                      </span>
                      <span class="legend-item" :class="{ 'legend-hidden': isSeriesHidden('Sau ăn') }"
                        @click="toggleGlucoseSeries('Sau ăn')" style="cursor: pointer; user-select: none;">
                        <span class="legend-dot after-meal"></span>
                        Sau ăn
                      </span>
                    </div>
                  </div>
                  <div class="chart-controls">
                    <!-- ✅ Period Filter (existing) -->
                    <basic-select v-model="glucoseChart.selectedOption" :options="getTimeRangeOptions()"
                      :value.sync="glucoseChart.periodFilterType" :solid="false" :allowEmpty="false" trackBy="value"
                      valueLabel="label" @input="(value) => onChartFilterChange('glucose', value)" />

                    <!-- ✅ NEW: Series Filter (Đói / Trước ăn / Sau ăn) -->
                    <basic-select v-model="glucoseChart.selectedSeriesFilter" :options="getGlucoseSeriesOptions()"
                      :value.sync="glucoseChart.seriesFilter" :solid="false" :allowEmpty="false" trackBy="value"
                      valueLabel="label" @input="(value) => onGlucoseSeriesFilterChange(value)"
                      placeholder="Lọc theo thời điểm" />


                  </div>
                </div>

                <b-overlay :show="glucoseChart.loading" variant="white" opacity="0.85">
                  <template #overlay>
                    <div class="text-center">
                      <b-spinner variant="primary"></b-spinner>
                      <p class="mt-2 text-muted small">Đang tải dữ liệu...</p>
                    </div>
                  </template>

                  <div class="chart-body"
                    v-if="glucoseChart.data && glucoseChart.data.series && glucoseChart.data.series.length > 0">
                    <apexchart :key="glucoseChart.chartKey" :options="getGlucoseChartOptions()"
                      :series="getGlucoseChartSeries()" type="line" height="300">
                    </apexchart>

                    <!-- Chart Stats -->
                    <div class="chart-stats">
                      <div class="stat-card">
                        <span class="stat-label">Lần khám trước</span>
                        <div class="stat-value-group">
                          <span class="stat-value">{{ lastestMetricsNextBloodSugar.value || '--' }} {{
                            lastestMetricsNextBloodSugar.unit || '--' }}</span>
                          <span class="stat-date">{{ formatDateWithTime(lastestMetricsNextBloodSugar.latestExaminationDate) || '--'
                          }}</span>
                        </div>
                      </div>
                      <div class="stat-card">
                        <span class="stat-label">Gần nhất</span>
                        <div class="stat-value-group">
                          <span class="stat-value">
                            {{ glucoseStatistics.latest.value }} {{ glucoseStatistics.latest.unit }}
                            <!-- Indicator -->
                            <span v-if="latestMetrics.glucoseTrend?.direction"
                                  :class="['stat-indicator', latestMetrics.glucoseTrend.direction]">
                              <svg v-if="latestMetrics.glucoseTrend.direction === 'up'" width="10" height="10" viewBox="0 0 12 12">
                                <path d="M6 2L10 7H2L6 2Z" fill="currentColor"/>
                              </svg>
                              <svg v-else-if="latestMetrics.glucoseTrend.direction === 'down'" width="10" height="10" viewBox="0 0 12 12">
                                <path d="M6 10L2 5H10L6 10Z" fill="currentColor"/>
                              </svg>
                              {{ latestMetrics.glucoseTrend.diff }}
                            </span>
                          </span>
                          <span class="stat-date">{{ glucoseStatistics.latest.date }}</span>
                        </div>
                      </div>
                      <div class="stat-card">
                        <span class="stat-label">Chỉ số trung bình hiện tại</span>
                        <div class="stat-value-group">
                          <span class="stat-value">
                            {{ glucoseStatistics.average.value }} {{ glucoseStatistics.average.unit }}
                          </span>
                          <span class="stat-date">{{ glucoseStatistics.average.period }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-else class="chart-no-data">
                    <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
                      <circle cx="30" cy="30" r="28" stroke="#E5E7EB" stroke-width="2" />
                      <path d="M15 30 L25 20 L35 30 L45 20" stroke="#9CA3AF" stroke-width="2" fill="none" />
                    </svg>
                    <p>Chưa có dữ liệu</p>
                  </div>
                </b-overlay>
              </div>

              <!-- Blood Pressure Chart -->
              <div class="chart-section">
                <div class="chart-header">
                  <div class="chart-title-group">
                    <h3>Huyết áp</h3>
                    <div class="chart-legend">
                      <span class="legend-item" :class="{ 'legend-hidden': isBloodPressureSeriesHidden('Tâm thu') }"
                        @click="toggleBloodPressureSeries('Tâm thu')" style="cursor: pointer; user-select: none;">
                        <span class="legend-dot systolic"></span>
                        Tâm thu
                      </span>
                      <span class="legend-item" :class="{ 'legend-hidden': isBloodPressureSeriesHidden('Tâm trương') }"
                        @click="toggleBloodPressureSeries('Tâm trương')" style="cursor: pointer; user-select: none;">
                        <span class="legend-dot diastolic"></span>
                        Tâm trương
                      </span>
                      <span class="legend-item" :class="{ 'legend-hidden': isBloodPressureSeriesHidden('Nhịp tim') }"
                        @click="toggleBloodPressureSeries('Nhịp tim')" style="cursor: pointer; user-select: none;">
                        <span class="legend-dot pulse"></span>
                        Nhịp tim
                      </span>
                    </div>
                  </div>
                  <div class="chart-controls">
                    <!-- ✅ Period Filter (existing) -->
                    <basic-select v-model="bloodPressureChart.selectedOption" :options="getTimeRangeOptions()"
                      :value.sync="bloodPressureChart.periodFilterType" :solid="false" :allowEmpty="false"
                      trackBy="value" valueLabel="label"
                      @input="(value) => onChartFilterChange('bloodPressure', value)" />

                    <!-- ✅ NEW: Series Filter (Tâm thu / Tâm trương) -->
                    <basic-select v-model="bloodPressureChart.selectedSeriesFilter"
                      :options="getBloodPressureSeriesOptions()" :value.sync="bloodPressureChart.seriesFilter"
                      :solid="false" :allowEmpty="false" trackBy="value" valueLabel="label"
                      @input="(value) => onBloodPressureSeriesFilterChange(value)" placeholder="Lọc theo chỉ số" />


                  </div>
                </div>

                <b-overlay :show="bloodPressureChart.loading" variant="white" opacity="0.85">
                  <template #overlay>
                    <div class="text-center">
                      <b-spinner variant="primary"></b-spinner>
                      <p class="mt-2 text-muted small">Đang tải dữ liệu...</p>
                    </div>
                  </template>

                  <div class="chart-body"
                    v-if="bloodPressureChart.data && bloodPressureChart.data.series && bloodPressureChart.data.series.length > 0">
                    <apexchart :options="getBloodPressureChartOptions()" :series="getBloodPressureChartSeries()"
                      type="line" height="300">
                    </apexchart>

                    <!-- Chart Stats -->
                    <div class="chart-stats">
                      <div class="stat-card">
                        <span class="stat-label">Lần khám trước</span>
                        <div class="stat-value-group">
                          <span class="stat-value">{{ lastestMetricsNextBloodPressure.value || '--' }} {{
                            lastestMetricsNextBloodPressure.unit || '--' }}</span>
                          <span class="stat-date">{{ formatDateWithTime(lastestMetricsNextBloodPressure.latestExaminationDate) || '--'
                          }}</span>
                        </div>
                      </div>
                      <div class="stat-card">
                        <span class="stat-label">Gần nhất</span>
                        <div class="stat-value-group">
                          <span class="stat-value">
                            {{ bloodPressureStatistics.latest.systolic }}/{{ bloodPressureStatistics.latest.diastolic }}
                            {{ bloodPressureStatistics.latest.unit }}
                            <!-- Indicator -->
                            <span v-if="latestMetrics.bloodPressureTrend?.direction"
                                  :class="['stat-indicator', latestMetrics.bloodPressureTrend.direction]">
                              <svg v-if="latestMetrics.bloodPressureTrend.direction === 'up'" width="10" height="10" viewBox="0 0 12 12">
                                <path d="M6 2L10 7H2L6 2Z" fill="currentColor"/>
                              </svg>
                              <svg v-else-if="latestMetrics.bloodPressureTrend.direction === 'down'" width="10" height="10" viewBox="0 0 12 12">
                                <path d="M6 10L2 5H10L6 10Z" fill="currentColor"/>
                              </svg>
                              {{ latestMetrics.bloodPressureTrend.diff }}
                            </span>
                          </span>
                          <span class="stat-date">{{ bloodPressureStatistics.latest.date }}</span>
                        </div>
                      </div>
                      <div class="stat-card">
                        <span class="stat-label">Chỉ số trung bình hiện tại</span>
                        <div class="stat-value-group">
                          <span class="stat-value">
                            {{ bloodPressureStatistics.average.systolic }}/{{ bloodPressureStatistics.average.diastolic
                            }}
                            {{ bloodPressureStatistics.average.unit }}
                          </span>
                          <span class="stat-date">{{ bloodPressureStatistics.average.period }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-else class="chart-no-data">
                    <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
                      <circle cx="30" cy="30" r="28" stroke="#E5E7EB" stroke-width="2" />
                      <path d="M15 30 L25 20 L35 30 L45 20" stroke="#9CA3AF" stroke-width="2" fill="none" />
                    </svg>
                    <p>Chưa có dữ liệu</p>
                  </div>
                </b-overlay>
              </div>

              <!-- Lab Results Section -->
              <div class="lab-results-section mt-4">
                <div class="section-header">
                  <div class="section-title-with-search">
                    <h3>Kết quả xét nghiệm chi tiết</h3>
                    <input type="text" placeholder="Tìm xét nghiệm" class="search-input" v-model="labResultsSearch"
                      @input="filterLabResults" />
                  </div>
                  <div class="section-actions">
                    <!-- Filter: Thời gian -->
                    <basic-select v-model="labResultsFilters.selectedMonth" :options="getMonthOptions()"
                      :value.sync="labResultsFilters.monthFilter" :solid="false" :allowEmpty="true" trackBy="value"
                      valueLabel="label" @input="onMonthFilterChange" placeholder="Chọn thời gian" />

                    <!-- Filter: Tình trạng -->
                    <basic-select v-model="labResultsFilters.selectedStatus" :options="getStatusOptions()"
                      :value.sync="labResultsFilters.statusFilter" :solid="false" :allowEmpty="true" trackBy="value"
                      valueLabel="label" @input="onStatusFilterChange" placeholder="Tình trạng" />

                    <!-- Filter: Danh mục -->
                    <basic-select v-model="labResultsFilters.selectedCategory" :options="getCategoryOptions()"
                      :value.sync="labResultsFilters.categoryFilter" :solid="false" :allowEmpty="true" trackBy="value"
                      valueLabel="label" @input="onCategoryFilterChange" placeholder="Danh mục" />

                    <button class="action-btn" @click="downloadLabResults">Tải xuống</button>
                  </div>
                </div>

                <!-- Two Column Layout: Lab Results + Medications Sidebar -->
                <div class="bottom-section-grid">
                <!-- Lab Results Table -->
                <div class="lab-results-wrapper">
                  <b-overlay :show="labResultsLoading" variant="white">
                    <table class="lab-results-table">
                      <thead>
                        <tr>
                          <th>Ngày</th>
                          <th>Nhóm</th>
                          <th>Y lệnh</th>
                          <th>Kết quả</th>
                          <th>Đơn vị</th>
                          <th>Khoảng tham chiếu</th>
                          <th>Đánh giá</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="filteredLabResults.length === 0">
                          <td colspan="7" class="no-data-message">
                            Không tìm thấy kết quả xét nghiệm
                          </td>
                        </tr>
                        <tr v-for="result in paginatedLabResults" :key="result.id">
                          <td>{{ result.date }}</td>
                          <td>{{ result.category }}</td>
                          <td>{{ result.testName }}</td>
                          <td class="value-cell" :class="result.status.toLowerCase()">{{ result.value }}</td>
                          <td>{{ result.unit }}</td>
                          <td>{{ result.range.includes("null") ? "Đang cập nhật" : result.range }}</td>
                          <td>
                            <span :class="['status-badge', result.status.toLowerCase()]">{{ result.statusText }}</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </b-overlay>

                  <div class="results-footer">
                    <div class="results-note">
                      Hiển thị {{ (currentPage - 1) * itemsPerPage + 1 }}-{{ Math.min(currentPage * itemsPerPage, filteredLabResults.length) }} trong tổng số {{ filteredLabResults.length }} kết quả
                    </div>

                    <!-- Pagination -->
                    <div class="pagination-controls" v-if="totalPages > 1">
                      <button class="pagination-btn" @click="currentPage--" :disabled="currentPage === 1">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M10 12L6 8L10 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                      </button>
                      <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
                      <button class="pagination-btn" @click="currentPage++" :disabled="currentPage === totalPages">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
                <!-- Medications Sidebar -->
                <div class="medications-sidebar">
                  <!-- Medications in Use -->
                  <div class="sidebar-section">
                    <div class="sidebar-header">
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <rect x="2" y="4" width="14" height="10" rx="2" stroke="currentColor" stroke-width="1.5" />
                        <path d="M6 4V3a1 1 0 011-1h4a1 1 0 011 1v1" stroke="currentColor" stroke-width="1.5" />
                      </svg>
                      <h3>Thuốc đang sử dụng ({{ medications.length }})</h3>
                    </div>

                    <b-overlay :show="medicationsLoading" variant="white">
                      <div class="medication-list">
                        <!-- Show message if no medications -->
                        <div v-if="medications.length === 0" class="no-data-message">
                          Không có thuốc đang sử dụng
                        </div>

                        <!-- Loop through medications -->
                        <div v-for="med in medications" :key="med.id" class="medication-item"
                          :class="{ 'expiring-soon': med.isExpiringSoon }">
                          <h4>{{ med.name }}</h4>
                          <!-- <p><strong>Title:</strong> {{ med.genericName }}</p> -->
                          <p><strong>Liều dùng:</strong> {{ med.dosage }}</p>
                          <p><strong>Tần suất:</strong> {{ med.frequency }}</p>
                          <!-- <p><strong>Số lượng:</strong> {{ med.quantity }}</p> -->
                          <!-- <p><strong>Đường dùng:</strong> {{ med.route }}</p> -->
                          <p><strong>Ngày bắt đầu:</strong> {{ med.startDate }}</p>
                          <!-- <p><strong>Ngày hết hạn:</strong> {{ med.endDate }}</p> -->

                          <!-- Warning nếu sắp hết hạn -->
                          <div v-if="med.isExpiringSoon" class="expiry-warning">
                            ⚠️ Còn {{ med.daysRemaining }} ngày
                          </div>

                          <!-- Hướng dẫn sử dụng nếu có -->
                          <p v-if="med.instructions !== '--'" class="instructions">
                            <strong>Hướng dẫn:</strong> {{ med.instructions }}
                          </p>
                        </div>
                      </div>
                    </b-overlay>
                  </div>

                  <!-- Treatment Recommendations -->
                  <div class="sidebar-section">
                    <div class="sidebar-header">
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path d="M9 2v14M2 9h14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                      </svg>
                      <h3>Chỉ dẫn của Bác sĩ ({{ doctorNotes.length }})</h3>
                    </div>

                    <b-overlay :show="doctorNotesLoading" variant="white">
                      <div class="recommendations-list">
                        <div v-if="doctorNotes.length === 0" class="no-data-message">
                          Chưa có ghi chú từ bác sĩ
                        </div>

                        <div v-for="note in doctorNotes" :key="note.id" class="recommendation-box">
                          <h4>{{ note.title }} - {{ note.date }}</h4>

                          <ul v-if="note.sections.items.length > 0">
                            <li v-for="item in note.sections.items" :key="item.id">
                              {{ item.text }}
                            </li>
                          </ul>

                          <div v-else class="note-content">
                            {{ note.content }}
                          </div>

                          <div v-if="note.sections.actions.length > 0" class="recommendation-actions">
                            <button v-for="action in note.sections.actions" :key="action.id" class="action-link"
                              @click="handleAction(action, note)">
                              {{ action.text }}
                            </button>
                          </div>
                        </div>
                      </div>
                    </b-overlay>
                  </div>
                </div>
                <!-- End Medications Sidebar -->
              </div>
              <!-- End bottom-section-grid -->
              </div>
              <!-- End lab-results-section -->

            </div>
            <!-- End Overview Tab Content -->

            <!-- Communication Tab Content -->


          </div>
          <!-- End tabs-container -->
          <div v-if="activeTab === 'communication'" class="tab-content-section">
            <DashboardCommunicationTab ref="communicationTab" v-if="activeTab === 'communication'"
              :patientId="userInformation.patientId" :accountId="userInformation.accountId"
              :patientInfo="userInformation"
              :preventInitialScroll="true"
              @data-refreshed="handleDataRefreshed" />
          </div>

          <!-- Rest Tab Content -->
          <div v-if="activeTab === 'rest'" class="tab-content-section">
            <div class="section-title-header">
              <h2>Khuyến nghị</h2>
            </div>
            <div class="empty-state">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                <circle cx="40" cy="40" r="38" stroke="#E5E7EB" stroke-width="2" />
                <path d="M25 40h30M40 25v30" stroke="#9CA3AF" stroke-width="2" stroke-linecap="round" />
              </svg>
              <h3>Chức năng đang phát triển</h3>
              <p>Thông tin về khuyến nghị sẽ được cập nhật sớm</p>
            </div>
          </div>

          <!-- Profile Tab Content -->
          <div v-if="activeTab === 'profile'" class="tab-content-section">
            <div class="section-title-header">
              <h2>Hồ sơ bệnh nhân</h2>
            </div>
            <div class="empty-state">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                <circle cx="40" cy="40" r="38" stroke="#E5E7EB" stroke-width="2" />
                <path d="M25 40h30M40 25v30" stroke="#9CA3AF" stroke-width="2" stroke-linecap="round" />
              </svg>
              <h3>Chức năng đang phát triển</h3>
              <p>Hồ sơ chi tiết bệnh nhân sẽ được cập nhật sớm</p>
            </div>
          </div>
        </div>
        <!-- End content-wrapper -->
      </div>
      <!-- End main-wrapper -->
    </div>
  </div>

</template>

<script>
import { healthService, BLOOD_PRESSURE_TIMEFRAME } from './services/healthService';
import DashboardCommunicationTab from './DashboardCommunicationTab.vue'
import PatientSelectList from './component/PatientSelectList.vue'
import { convertTimestampToYear, convertTimestampToDateTime, formatDateTimeWithTime } from '../../../core/utils/dateUtils'
import { hisLabService } from './services/hisService';

export default {
  name: 'PatientDashboard',
  components: {
    DashboardCommunicationTab,
    PatientSelectList
  },
  data() {
    return {
      // Active Tab
      activeTab: 'overview', // overview, communication, rest, profile
      isRefreshing: false,
      // User Information
      userInformation: {
        patientId: '',
        accountId: '',
        name: '',
        phoneNumber: '',
        birthday: '',
        gender: '',
        typeOfSick: '',
        age: '',
        tag: '', // diab, his, both
        source: '', // DIAB, HIS
        isCustomerReceive: false, // true = cho phép truy cập tab Communication
        zaloGroupId: '', // rỗng/null = chưa/không còn thuộc nhóm Zalo nào (hết khoá, chưa vào nhóm...)
      },

      // Patient Data
      patientData: {
        riskLevel: 'HIGH RISK',
        nextAppointment: '26 Tháng 11, 2025',
        alerts: 3
      },

      // Latest Metrics
      latestMetrics: {
        glucose: null,
        glucoseUnit: 'mg/dL',
        glucoseType: '',
        systolic: null,
        diastolic: null,
        bpUnit: 'mmHg',
        bloodPressureType: '',
        weight: null,
        weightUnit: 'kg',
        weightType: '',
        waist: null,
        waistUnit: 'cm',
        waistType: '',
        hba1c: null,
        hba1cUnit: '%',
        hba1cType: '',
        bmi: null,
        bmiUnit: '',
        bmiType: '',
        liver: null,
        liverUnit: 'IU/L',
        kidney: null,
        kidneyUnit: 'mg/dL',
      },

      // Metrics Change Indicators (so với lần khám trước)
      metricsChange: {
        glucose: { value: null, direction: null }, // direction: 'up', 'down', 'stable'
        systolic: { value: null, direction: null },
        diastolic: { value: null, direction: null },
        weight: { value: null, direction: null },
        hba1c: { value: null, direction: null },
        bmi: { value: null, direction: null },
        waist: { value: null, direction: null },
        liver: { value: null, direction: null },
        kidney: { value: null, direction: null },
      },
      lastestMetricsNextBloodSugar: {
        latestExaminationDate: '',
        nextExaminationDate: '',
        unit: '',
        value: null,
      },
      lastestMetricsNextBloodPressure: {
        latestExaminationDate: '',
        nextExaminationDate: '',
        unit: '',
        value: null
      },
      // Chart Data
      glucoseChart: {
        data: null,
        rawData: null,
        loading: false,
        selectedOption: { value: 3, label: '30 ngày' },
        periodFilterType: 3,
        selectedSeriesFilter: { value: 'all', label: 'Tất cả' },
        seriesFilter: 'all',
        hiddenSeries: [],
        chartKey: 0 // ✅ ADD THIS
      },

      bloodPressureChart: {
        data: null,
        rawData: null, // ✅ NEW: Store raw data for filtering
        loading: false,
        selectedOption: { value: 3, label: '30 ngày' },
        periodFilterType: 3,
        // ✅ NEW: Series filter
        selectedSeriesFilter: { value: 'all', label: 'Tất cả' },
        seriesFilter: 'all',
        hiddenSeries: [] // ✅ NEW: Track hidden series for legend click toggle
      },

      // Lab Results
      labResults: [
        { id: 1, date: '23/07/2025', category: 'Đường huyết', testName: 'Fasting Glucose', value: '185', unit: 'mg/dL', range: '70 - 100', status: 'danger', statusText: 'Cao bất thường' },
        { id: 2, date: '23/07/2025', category: 'HbA1C', testName: 'HbA1C', value: '8.7', unit: '%', range: '4 - 5.6', status: 'danger', statusText: 'Cao bất thường' },
        { id: 3, date: '23/07/2025', category: 'Gan', testName: 'ALT', value: '48', unit: 'IU/L', range: '7 - 45', status: 'warning', statusText: 'Cao' },
        { id: 4, date: '23/07/2025', category: 'Gan', testName: 'AST', value: '52', unit: 'IU/L', range: '8 - 48', status: 'warning', statusText: 'Cao' },
        { id: 5, date: '23/07/2025', category: 'Thận', testName: 'Creatinine', value: '1.8', unit: 'mg/dL', range: '0.6 - 1.2', status: 'warning', statusText: 'Cao' },
        { id: 6, date: '23/07/2025', category: 'Thận', testName: 'Urine Albumin', value: '48', unit: 'mg/24h', range: '0 - 30', status: 'warning', statusText: 'Cao' }
      ],
      labResultsLoading: false,

      // Medications
      medications: [
        { id: 1, name: 'Metformin', dosage: '20 mg', frequency: '2 lần/ngày', quantity: '60 XXX', expiryDate: '15/10/25' },
        { id: 2, name: 'Lisinopril', dosage: '20 mg', frequency: '2 lần/ngày', quantity: '60 XXX', expiryDate: '15/10/25' },
        { id: 3, name: 'Atorvastatin', dosage: '20 mg', frequency: '2 lần/ngày', quantity: '60 XXX', expiryDate: '15/10/25' },
        { id: 4, name: 'Atorvastatin', dosage: '20 mg', frequency: '2 lần/ngày', quantity: '60 XXX', expiryDate: '15/10/25' },
        { id: 5, name: 'Metformin', dosage: '20 mg', frequency: '2 lần/ngày', quantity: '60 XXX', expiryDate: '15/10/25' }
      ],
      doctorNotes: [],
      doctorNotesLoading: false,
      medicationsLoading: false,
      labResultsSearch: '',
      labResultsFilters: {
        selectedMonth: null,
        monthFilter: null,
        selectedStatus: null,
        statusFilter: null,
        selectedCategory: null,
        categoryFilter: null
      },
      labResultsOriginal: [],

      // Filtered lab results
      filteredLabResults: [],

      // Pagination
      currentPage: 1,
      itemsPerPage: 10,
    };
  },

  mounted() {
    this.initializeDashboard();
  },
  watch: {
    activeTab(newTab, oldTab) {
      console.log(`🔄 Tab changed: ${oldTab} → ${newTab}`);
    },
    filteredLabResults() {
      this.currentPage = 1;
    }
  },
  created() {
    this.$store.dispatch('layout/toggleSidebar', false);

  },
  // ===== SOLUTION: Calculate Chart Statistics Dynamically =====

  // ✅ 1. ADD COMPUTED PROPERTIES FOR STATISTICS
  computed: {
    // Paginated results
    paginatedLabResults() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredLabResults.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredLabResults.length / this.itemsPerPage);
    },

    // ✅ Kiểm tra có quyền truy cập tab Communication không
    // Chỉ cần là khách trong danh sách tiếp nhận. Không xét nhóm Zalo nữa: khách
    // hoàn thành/không tham gia bị gỡ nhóm nhưng bác sĩ vẫn cần trao đổi được.
    canAccessCommunicationTab() {
      return !!this.userInformation.isCustomerReceive;
    },

    // ✅ Lý do bị khoá tab Communication (hiển thị tooltip tương ứng)
    communicationTabLockedReason() {
      if (!this.userInformation.isCustomerReceive) {
        return 'Chức năng này chỉ khả dụng cho bệnh nhân DiaB';
      }
      return '';
    },

    // Glucose statistics
    glucoseStatistics() {
      if (!this.glucoseChart.data || !this.glucoseChart.data.series) {
        return {
          latest: { value: '--', unit: 'mg/dL', date: '--' },
          average: { value: '--', unit: 'mg/dL', period: '--' },
          limit: { value: '--', unit: 'mg/dL', trend: '--' }
        };
      }

      // Lấy tất cả data points
      let allDataPoints = [];
      this.glucoseChart.data.series.forEach(series => {
        if (series.data && series.data.length > 0) {
          series.data.forEach(point => {
            if (point.y !== null && point.y !== undefined) {
              allDataPoints.push({
                value: point.y,
                timestamp: point.x,
                seriesName: series.name
              });
            }
          });
        }
      });

      if (allDataPoints.length === 0) {
        return {
          latest: { value: '--', unit: 'mg/dL', date: '--' },
          average: { value: '--', unit: 'mg/dL', period: '--' },
          limit: { value: '--', unit: 'mg/dL', trend: '--' }
        };
      }

      allDataPoints.sort((a, b) => b.timestamp - a.timestamp);

      // LATEST
      const latest = allDataPoints[0];
      const latestDate = new Date(latest.timestamp);

      // ✅ AVERAGE: Tính theo period filter được chọn
      const now = Date.now();
      const periodFilterType = this.glucoseChart.periodFilterType;

      let periodDays = 7;
      let periodLabel = '7 ngày';

      switch (periodFilterType) {
        case 1: periodDays = 7; periodLabel = '7 ngày'; break;
        case 2: periodDays = 14; periodLabel = '14 ngày'; break;
        case 3: periodDays = 30; periodLabel = '30 ngày'; break;
        case 4: periodDays = 90; periodLabel = '90 ngày'; break;
      }

      const periodMs = periodDays * 24 * 60 * 60 * 1000;
      const periodStartTime = now - periodMs;

      const periodData = allDataPoints.filter(point =>
        point.timestamp >= periodStartTime && point.timestamp <= now
      );

      let averageValue = '--';
      if (periodData.length > 0) {
        const sum = periodData.reduce((acc, point) => acc + point.value, 0);
        averageValue = Math.round(sum / periodData.length);
      }

      // LIMIT
      const maxValue = periodData.length > 0
        ? Math.max(...periodData.map(p => p.value))
        : '--';

      // Trend (so với period trước đó)
      const previousPeriodStart = periodStartTime - periodMs;
      const previousPeriodData = allDataPoints.filter(point =>
        point.timestamp >= previousPeriodStart && point.timestamp < periodStartTime
      );

      let trend = 0;
      if (previousPeriodData.length > 0 && periodData.length > 0) {
        const prevAvg = previousPeriodData.reduce((acc, p) => acc + p.value, 0) / previousPeriodData.length;
        const currAvg = periodData.reduce((acc, p) => acc + p.value, 0) / periodData.length;
        trend = Math.round(currAvg - prevAvg);
      }

      return {
        latest: {
          value: Math.round(latest.value),
          unit: this.glucoseChart.data.unit || 'mg/dL',
          date: this.formatDateWithTime(latestDate),
          seriesName: latest.seriesName
        },
        average: {
          value: averageValue,
          unit: this.glucoseChart.data.unit || 'mg/dL',
          period: periodLabel,  // ✅ Hiển thị đúng period
          count: periodData.length
        },
        limit: {
          value: maxValue,
          unit: this.glucoseChart.data.unit || 'mg/dL',
          trend: trend,
          trendDirection: trend > 0 ? 'up' : trend < 0 ? 'down' : 'stable'
        }
      };
    },


    // Blood Pressure statistics
    bloodPressureStatistics() {
      if (!this.bloodPressureChart.data || !this.bloodPressureChart.data.series) {
        return {
          latest: { systolic: '--', diastolic: '--', unit: 'mmHg', date: '--' },
          average: { systolic: '--', diastolic: '--', unit: 'mmHg', period: '--' },
          limit: { systolic: '--', diastolic: '--', unit: 'mmHg', trend: '--' }
        };
      }

      const systolicSeries = this.bloodPressureChart.data.series.find(s =>
        s.name === 'Tâm thu' || s.name.includes('Systolic')
      );
      const diastolicSeries = this.bloodPressureChart.data.series.find(s =>
        s.name === 'Tâm trương' || s.name.includes('Diastolic')
      );

      if (!systolicSeries || !diastolicSeries) {
        return {
          latest: { systolic: '--', diastolic: '--', unit: 'mmHg', date: '--' },
          average: { systolic: '--', diastolic: '--', unit: 'mmHg', period: '--' },
          limit: { systolic: '--', diastolic: '--', unit: 'mmHg', trend: '--' }
        };
      }

      const systolicData = systolicSeries.data
        .filter(p => p.y !== null && p.y !== undefined)
        .map(p => ({ value: p.y, timestamp: p.x }))
        .sort((a, b) => b.timestamp - a.timestamp);

      const diastolicData = diastolicSeries.data
        .filter(p => p.y !== null && p.y !== undefined)
        .map(p => ({ value: p.y, timestamp: p.x }))
        .sort((a, b) => b.timestamp - a.timestamp);

      if (systolicData.length === 0 || diastolicData.length === 0) {
        return {
          latest: { systolic: '--', diastolic: '--', unit: 'mmHg', date: '--' },
          average: { systolic: '--', diastolic: '--', unit: 'mmHg', period: '--' },
          limit: { systolic: '--', diastolic: '--', unit: 'mmHg', trend: '--' }
        };
      }

      // LATEST
      const latestSystolic = systolicData[0];
      const latestDiastolic = diastolicData[0];
      const latestDate = new Date(latestSystolic.timestamp);

      // ✅ AVERAGE: Tính theo period filter được chọn
      const now = Date.now();
      const periodFilterType = this.bloodPressureChart.periodFilterType;

      let periodDays = 7;
      let periodLabel = '7 ngày';

      switch (periodFilterType) {
        case 1: periodDays = 7; periodLabel = '7 ngày'; break;
        case 2: periodDays = 14; periodLabel = '14 ngày'; break;
        case 3: periodDays = 30; periodLabel = '30 ngày'; break;
        case 4: periodDays = 90; periodLabel = '90 ngày'; break;
      }

      const periodMs = periodDays * 24 * 60 * 60 * 1000;
      const periodStartTime = now - periodMs;

      const periodSystolic = systolicData.filter(p =>
        p.timestamp >= periodStartTime && p.timestamp <= now
      );
      const periodDiastolic = diastolicData.filter(p =>
        p.timestamp >= periodStartTime && p.timestamp <= now
      );

      let avgSystolic = '--';
      let avgDiastolic = '--';

      if (periodSystolic.length > 0) {
        const sum = periodSystolic.reduce((acc, p) => acc + p.value, 0);
        avgSystolic = Math.round(sum / periodSystolic.length);
      }

      if (periodDiastolic.length > 0) {
        const sum = periodDiastolic.reduce((acc, p) => acc + p.value, 0);
        avgDiastolic = Math.round(sum / periodDiastolic.length);
      }

      // LIMIT
      const maxSystolic = periodSystolic.length > 0
        ? Math.max(...periodSystolic.map(p => p.value))
        : '--';
      const maxDiastolic = periodDiastolic.length > 0
        ? Math.max(...periodDiastolic.map(p => p.value))
        : '--';

      return {
        latest: {
          systolic: Math.round(latestSystolic.value),
          diastolic: Math.round(latestDiastolic.value),
          unit: this.bloodPressureChart.data.unit || 'mmHg',
          date: this.formatDateWithTime(latestDate)
        },
        average: {
          systolic: avgSystolic,
          diastolic: avgDiastolic,
          unit: this.bloodPressureChart.data.unit || 'mmHg',
          period: periodLabel,  // ✅ Hiển thị đúng period
          count: periodSystolic.length
        },
        limit: {
          systolic: maxSystolic,
          diastolic: maxDiastolic,
          unit: this.bloodPressureChart.data.unit || 'mmHg'
        }
      };
    }
  },

  methods: {
    isSeriesHidden(seriesName) {
      const hidden = this.glucoseChart.hiddenSeries.includes(seriesName);
      return hidden;
    },

    applyGlucoseSeriesFilter() {

      if (!this.glucoseChart.rawData) {
        return;
      }


      const rawData = this.glucoseChart.rawData;
      const filter = this.glucoseChart.seriesFilter;


      const fullChartData = healthService.formatGlucoseTrendToChart(rawData);

      let filteredSeries = fullChartData.series;

      // Apply dropdown filter
      if (filter === 'fasting') {
        filteredSeries = filteredSeries.filter(s => s.name === 'Đường huyết đói');
      } else if (filter === 'before_meal') {
        filteredSeries = filteredSeries.filter(s => s.name === 'Trước ăn');
      } else if (filter === 'after_meal') {
        filteredSeries = filteredSeries.filter(s => s.name === 'Sau ăn');
      }

      // Apply hidden series
      filteredSeries = filteredSeries.map(s => {
        const isHidden = this.glucoseChart.hiddenSeries.includes(s.name);
        const newData = isHidden ? [] : s.data;
        return {
          ...s,
          data: newData
        };
      });

      this.glucoseChart.data = {
        ...fullChartData,
        series: filteredSeries
      };
    },


    // ✅ 4. NEW METHOD: Toggle Series Visibility on Click
    toggleGlucoseSeries(clickedSeriesName) {
      const currentHidden = this.glucoseChart.hiddenSeries || [];

      // ✅ Toggle logic: Nếu đang ẩn thì hiện, nếu đang hiện thì ẩn
      if (currentHidden.includes(clickedSeriesName)) {
        // Đang ẩn → Hiện lại
        this.glucoseChart.hiddenSeries = currentHidden.filter(
          name => name !== clickedSeriesName
        );
      } else {
        // Đang hiện → Ẩn đi
        this.glucoseChart.hiddenSeries = [...currentHidden, clickedSeriesName];
      }

      // Force ApexCharts cập nhật
      this.glucoseChart.chartKey++;
    },

    // ✅ Blood Pressure Series Toggle Methods
    isBloodPressureSeriesHidden(seriesName) {
      return this.bloodPressureChart.hiddenSeries.includes(seriesName);
    },

    toggleBloodPressureSeries(clickedSeriesName) {
      const currentHidden = this.bloodPressureChart.hiddenSeries || [];

      if (currentHidden.includes(clickedSeriesName)) {
        // Đang ẩn → Hiện lại
        this.bloodPressureChart.hiddenSeries = currentHidden.filter(
          name => name !== clickedSeriesName
        );
      } else {
        // Đang hiện → Ẩn đi
        this.bloodPressureChart.hiddenSeries = [...currentHidden, clickedSeriesName];
      }
    },

    onGlucoseSeriesFilterChange(value) {
      this.glucoseChart.seriesFilter = value;
      this.applyGlucoseSeriesFilter();
    },
    getGlucoseSeriesOptions() {
      return [
        { value: 'all', label: 'Tất cả' },
        { value: 'fasting', label: 'Đường huyết đói' },
        { value: 'before_meal', label: 'Đường huyết trước ăn' },
        { value: 'after_meal', label: 'Đường huyết sau ăn' }
      ];
    },

    applyBloodPressureSeriesFilter() {
      if (!this.bloodPressureChart.rawData) return;

      const rawData = this.bloodPressureChart.rawData;
      const filter = this.bloodPressureChart.seriesFilter;

      // ✅ Apply timeFrame filter
      let filteredData = [...rawData];

      if (filter !== BLOOD_PRESSURE_TIMEFRAME.ALL) {
        filteredData = rawData.filter(item => {
          return item.timeFrameId === filter;
        });
      }

      // ✅ Format the filtered data
      const filteredChartData = healthService.formatBloodPressureTrendToChart(filteredData);

      this.bloodPressureChart.data = filteredChartData;
    },


    async onBloodPressureSeriesFilterChange(value) {
      this.bloodPressureChart.seriesFilter = value;
      await this.loadBloodPressureData();
    },
    getBloodPressureSeriesOptions() {
      return [
        { value: BLOOD_PRESSURE_TIMEFRAME.ALL, label: 'Tất cả' },
        { value: BLOOD_PRESSURE_TIMEFRAME.WAKE_UP, label: 'Thức giấc' },
        { value: BLOOD_PRESSURE_TIMEFRAME.ANY_TIME, label: 'Bất kỳ' },
      ];
    },


    formatTrend(trend, direction) {
      if (typeof trend !== 'number') return '';

      const icon = direction === 'up'
        ? '↑'
        : direction === 'down'
          ? '↓'
          : '→';

      const className = direction === 'up'
        ? 'up'
        : direction === 'down'
          ? 'down'
          : 'stable';

      return {
        text: `${icon} ${Math.abs(trend)}`,
        className: className
      };
    },

    getGlucoseTimeFrameOptions() {
      return [
        { value: 'all', label: 'Tất cả' },
        { value: 'fasting', label: 'Đường huyết đói' },
        { value: 'before_meal', label: 'Trước ăn' },
        { value: 'after_meal', label: 'Sau ăn' }
      ];
    },

    onGlucoseTimeFrameChange(value) {
      this.glucoseChart.timeFrameFilter = value;
      this.loadGlucoseData();
    },

    // Handle blood pressure timeFrame change
    onBloodPressureTimeFrameChange(value) {
      this.bloodPressureChart.timeFrameFilter = value;
      this.loadBloodPressureData();
    },
    switchTab(tabName) {
      console.log('🔄 Switching to tab:', tabName);
      this.activeTab = tabName;
    },
    async handleRefresh() {
      if (!this.userInformation.patientId) return;

      try {
        this.isRefreshing = true;

        // Refresh all components
        await Promise.all([
          this.loadUserData(),
          this.loadLabResults(),
          this.loadMedications(),
          this.loadDoctorNotes(),
          this.loadLabResultsAlt(),
          this.loadLabResultsCreatinine(),
          this.loadBloodPressureVitalSigns(),
          this.loadGlucoseVitalSigns(),
        ]);

        // Update timestamp
        this.lastUpdatedTime = Date.now();

      } catch (error) {
        console.error('❌ Refresh failed:', error);
      } finally {
        this.isRefreshing = false;
      }
    },

    async initializeDashboard() {
      // ✅ Kiểm tra localStorage trước
      const savedPatient = this.loadPatientFromStorage();

      if (savedPatient) {
        // Có patient trong localStorage, load lại data
        console.log('✅ Found saved patient, loading data...');
        await this.handlePatientSelect(savedPatient);
      } else if (!this.userInformation.patientId) {
        // Không có patient, show modal chọn
        this.$bvModal.show('customer-select-modal');
      } else {
        // Load initial data only if customer is selected
        await this.loadUserData();
      }
    },

    async loadUserData() {
      if (!this.userInformation.patientId) {
        this.$toastr.w({
          title: 'Thông báo',
          msg: 'Vui lòng chọn bệnh nhân trước',
        });
        return;
      }
      if (this.activeTab === 'communication' && this.$refs.communicationTab) {
        await this.$refs.communicationTab.refreshAllData();
      }
      try {
        await Promise.all([
          this.loadGlucoseData(),
          this.loadBloodPressureData(),
          this.loadHbA1cData(),
          this.loadBmiData()
        ]);
        this.$toastr.s(
          {
            title: 'Thành công',
            msg: 'Dữ liệu bệnh nhân được tải thành công'
          }

        );
      } catch (error) {
        console.error('❌ Error loading user data:', error);

        this.$toastr.w({
          title: 'Lỗi',
          msg: error
        }

        );
      }
    },
    handleDataRefreshed(timestamp) {
      this.lastUpdatedTime = timestamp;
    },
    async loadGlucoseData() {
      this.glucoseChart.loading = true;

      try {
        const patientId = this.userInformation.patientId;
        const periodFilterType = this.glucoseChart.periodFilterType;

        const currentDateTime = Math.floor(Date.now() / 1000);
        let fromDate;

        switch (periodFilterType) {
          case 1: fromDate = currentDateTime - (7 * 24 * 60 * 60); break;
          case 2: fromDate = currentDateTime - (14 * 24 * 60 * 60); break;
          case 3: fromDate = currentDateTime - (30 * 24 * 60 * 60); break;
          case 4: fromDate = currentDateTime - (90 * 24 * 60 * 60); break;
          default: fromDate = currentDateTime - (90 * 24 * 60 * 60);
        }

        const searchParams = {
          currentDateTime: currentDateTime,
          periodFilterType: periodFilterType,
          fromDate: fromDate,
          toDate: currentDateTime,
          takeAll: true,
          trendType: periodFilterType,
          patientId: patientId,
          size: 1000
        };

        const rawData = await healthService.getGlucoseTrend(this.$api, patientId, searchParams);

        // ✅ Store raw data for filtering
        this.glucoseChart.rawData = rawData;

        // ✅ Apply series filter
        this.applyGlucoseSeriesFilter();

        const latestGlucose = healthService.getLatestGlucose(rawData);
        if (latestGlucose) {
          this.latestMetrics.glucose = latestGlucose.value;
          this.latestMetrics.glucoseUnit = latestGlucose.unit;
          this.latestMetrics.glucoseType = latestGlucose.type; // Status: Cao, Tốt, Thấp
          this.latestMetrics.glucoseDate = latestGlucose.date;
          // Tính trend tăng/giảm từ 2 giá trị gần nhất
          this.latestMetrics.glucoseTrend = this.calculateTrendIndicator(rawData, 'glucose');
        }

        // ✅ Tính indicator thay đổi
        const glucoseChange = healthService.getGlucoseChange(rawData);
        this.metricsChange.glucose = glucoseChange;

      } catch (error) {
        console.error('❌ Error loading glucose data:', error);
        this.glucoseChart.data = healthService.getDefaultGlucoseChart();
      } finally {
        this.glucoseChart.loading = false;
      }
    },

    // ✅ 6. UPDATED: Glucose Chart Options with Click Event
    getGlucoseChartOptions() {
      if (!this.glucoseChart.data) return {};

      let totalDataPoints = 0;
      if (this.glucoseChart.data.series) {
        this.glucoseChart.data.series.forEach(series => {
          if (series.data && series.data.length > totalDataPoints) {
            totalDataPoints = series.data.length;
          }
        });
      }

      const colors = this.glucoseChart.data.series ?
        this.glucoseChart.data.series.map(s => s.color) :
        ['#3b82f6', '#10b981', '#8b5cf6'];

      let allValues = [];
      if (this.glucoseChart.data.series) {
        this.glucoseChart.data.series.forEach(series => {
          series.data.forEach(point => {
            if (point.y !== null && point.y !== undefined) {
              allValues.push(point.y);
            }
          });
        });
      }

    //  const minValue = allValues.length > 0 ? Math.min(...allValues) : 50;
      //  const maxValue = allValues.length > 0 ? Math.max(...allValues) : 350;

      const yAxisMin = 50;
      // const yAxisMax = Math.ceil(maxValue + 100);
      const yAxisMax = 400;


      const periodFilterType = this.glucoseChart.periodFilterType;
      let tickAmount = 7;

      switch (periodFilterType) {
        case 1: tickAmount = 7; break;
        case 2: tickAmount = 7; break;
        case 3: tickAmount = 10; break;
        case 4: tickAmount = 12; break;
      }

      return {
        chart: {
          type: 'line',
          height: 300,
          toolbar: { show: false },
          zoom: { enabled: totalDataPoints > 30 },
          events: {
            markerClick: (event, chartContext, { seriesIndex }) => {
              // Chỉ cần seriesIndex là đủ
              const seriesName = chartContext.w.config.series[seriesIndex]?.name;
              if (seriesName) {
                this.toggleGlucoseSeries(seriesName);
              }
            }
          }
        },
        colors: colors,
        stroke: {
          width: 2,
          curve: 'smooth'
        },
        xaxis: {
          type: 'datetime',
          labels: {
            rotate: totalDataPoints > 15 ? -45 : 0,
            rotateAlways: false,
            hideOverlappingLabels: true,
            style: {
              fontSize: '11px',
              colors: '#6b7280'
            },
            formatter: function (value, timestamp) {
              if (!timestamp) return '';
              const date = new Date(timestamp);
              return date.toLocaleDateString('vi-VN', {
                day: '2-digit',
                month: '2-digit'
              });
            },
            datetimeUTC: false
          },
          tickAmount: tickAmount,
          tickPlacement: 'on'
        },
        yaxis: {
          min: yAxisMin,
          max: yAxisMax,
          tickAmount: 6,
          labels: {
            style: {
              fontSize: '12px',
              colors: '#6b7280'
            },
            formatter: (value) => `${Math.round(value)} ${this.glucoseChart.data.unit || 'mg/dL'}`
          }
        },
        grid: {
          borderColor: '#e5e7eb',
          strokeDashArray: 4
        },
        legend: {
          show: false // ✅ Use custom legend instead
        },
        // ✅ OPTIMIZED TOOLTIP - Major performance improvement
        tooltip: {
          enabled: true,
          shared: false, // ✅ Don't show all series at once - reduces calculations
          intersect: true, // ✅ Only show tooltip when directly over point
          followCursor: false, // ✅ Tooltip stays at point location
          custom: function ({ series, seriesIndex, dataPointIndex, w }) {
            // ✅ Early return for no data
            if (!series[seriesIndex] || !series[seriesIndex][dataPointIndex]) {
              return '';
            }

            const value = series[seriesIndex][dataPointIndex];
            const timestamp = w.globals.seriesX[seriesIndex][dataPointIndex];
            const seriesName = w.config.series[seriesIndex].name;

            // ✅ Use Date directly instead of multiple toLocale calls
            const date = new Date(timestamp);
            const dateStr = date.toLocaleDateString('vi-VN');
            const timeStr = date.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });

            // ✅ Pre-calculate status once
            let status = '';
            let statusColor = '';

            if (seriesName === 'Đường huyết đói' || seriesName === 'Trước ăn') {
              if (value > 130) {
                status = 'Rất Cao';
                statusColor = '#dc2626';
              } else if (value >= 70 && value <= 130) {
                status = 'Bình thường';
                statusColor = '#10b981';
              } else if (value >= 54 && value < 70) {
                status = 'Thấp';
                statusColor = '#f59e0b';
              } else {
                status = 'Rất Thấp';
                statusColor = '#dc2626';
              }
            } else if (seriesName === 'Sau ăn') {
              if (value > 180) {
                status = 'Rất Cao';
                statusColor = '#dc2626';
              } else if (value >= 70 && value <= 179) {
                status = 'Bình thường';
                statusColor = '#10b981';
              } else if (value >= 54 && value < 70) {
                status = 'Thấp';
                statusColor = '#f59e0b';
              } else {
                status = 'Rất Thấp';
                statusColor = '#dc2626';
              }
            }

            // ✅ Use template literal for cleaner, faster HTML generation
            return `
          <div style="background: white; padding: 12px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); min-width: 180px;">
            <div style="font-size: 11px; color: #6b7280; margin-bottom: 6px;">
              ${dateStr} ${timeStr}
            </div>
            <div style="font-weight: 600; font-size: 13px; margin-bottom: 6px; color: #374151;">
              ${seriesName}
            </div>
            <div style="font-size: 18px; font-weight: 700; color: #111827; margin-bottom: 8px;">
              ${value} mg/dL
            </div>
            <div style="padding: 4px 10px; border-radius: 12px; background: ${statusColor}20; color: ${statusColor}; font-size: 12px; font-weight: 600; display: inline-block;">
              ${status}
            </div>
          </div>
        `;
          }
        },
        markers: {
          size: totalDataPoints > 30 ? 4 : 6,
          hover: {
            size: totalDataPoints > 30 ? 8 : 10
          },
          strokeWidth: 2,
          strokeOpacity: 0.9,
          fillOpacity: 1
        },
        // ✅ Optimize rendering
        states: {
          hover: {
            filter: {
              type: 'darken',
              value: 0.15
            }
          }
        }
      };
    },


    // ✅ Helper: Map timeFrame từ API sang filter value
    mapGlucoseTimeFrame(apiTimeFrame) {
      // Map từ các giá trị API sang filter values
      const mapping = {
        'Fasting': 'fasting',
        'BeforeMeal': 'before_meal',
        'AfterMeal': 'after_meal',
        'đường huyết đói': 'fasting',
        'trước ăn': 'before_meal',
        'sau ăn': 'after_meal'
      };
      return mapping[apiTimeFrame] || apiTimeFrame;
    },

    async loadBloodPressureData() {
      this.bloodPressureChart.loading = true;

      try {
        const patientId = this.userInformation.patientId;
        const periodFilterType = this.bloodPressureChart.periodFilterType;

        const currentDateTime = Math.floor(Date.now() / 1000);
        let fromDate;

        switch (periodFilterType) {
          case 1: fromDate = currentDateTime - (7 * 24 * 60 * 60); break;
          case 2: fromDate = currentDateTime - (14 * 24 * 60 * 60); break;
          case 3: fromDate = currentDateTime - (30 * 24 * 60 * 60); break;
          case 4: fromDate = currentDateTime - (90 * 24 * 60 * 60); break;
          default: fromDate = currentDateTime - (90 * 24 * 60 * 60);
        }

        const searchParams = {
          currentDateTime: currentDateTime,
          periodFilterType: periodFilterType,
          fromDate: fromDate,
          toDate: currentDateTime,
          takeAll: true,
          trendType: periodFilterType,
          patientId: patientId,
          // ✅ Send timeFrameId to API (if API supports it)
          timeFrameId: this.bloodPressureChart.seriesFilter === BLOOD_PRESSURE_TIMEFRAME.ALL
            ? null
            : this.bloodPressureChart.seriesFilter,
          size: 1000
        };

        const rawData = await healthService.getBloodPressureTrend(this.$api, patientId, searchParams);

        // ✅ Store raw data
        this.bloodPressureChart.rawData = rawData;

        // ✅ Apply filter
        this.applyBloodPressureSeriesFilter();

        // ✅ Update latest metrics
        const latestBP = healthService.getLatestBloodPressure(rawData);
        if (latestBP) {
          this.latestMetrics.systolic = latestBP.systolic;
          this.latestMetrics.diastolic = latestBP.diastolic;
          this.latestMetrics.bpUnit = latestBP.unit;
          this.latestMetrics.bloodPressureType = latestBP.bloodPressureType;
          this.latestMetrics.bloodPressureDate = latestBP.date;
          // Tính trend tăng/giảm
          this.latestMetrics.bloodPressureTrend = this.calculateTrendIndicator(rawData, 'bloodPressure');
        }

        // ✅ Tính indicator thay đổi
        const bpChange = healthService.getBloodPressureChange(rawData);
        this.metricsChange.systolic = bpChange.systolic;
        this.metricsChange.diastolic = bpChange.diastolic;
      } catch (error) {
        console.error('❌ Error loading blood pressure data:', error);
        this.bloodPressureChart.data = {
          series: [],
          colors: ['#ef4444', '#3b82f6', '#10b981'],
          yAxisMin: 60,
          yAxisMax: 180,
          unit: 'mmHg'
        };
      } finally {
        this.bloodPressureChart.loading = false;
      }
    },


    // ✅ Helper: Map timeFrame từ API sang filter value
    mapBloodPressureTimeFrame(apiTimeFrame) {
      const mapping = {
        'Waking': 'waking',
        'Anytime': 'anytime',
        'thức giấc': 'waking',
        'bất kỳ': 'anytime'
      };
      return mapping[apiTimeFrame] || apiTimeFrame;
    },

    async loadHbA1cData() {
      try {
        const patientId = this.userInformation.patientId;
        const periodFilterType = 3; // Default 24 months

        const currentDateTime = Math.floor(Date.now() / 1000);
        const fromDate = currentDateTime - (730 * 24 * 60 * 60); // 24 months ≈ 730 days

        const searchParams = {
          currentDateTime: currentDateTime,
          periodFilterType: periodFilterType,
          fromDate: fromDate,
          toDate: currentDateTime,
          takeAll: true,
          trendType: periodFilterType,
          patientId: patientId
        };

        const rawData = await healthService.getHba1cTrend(this.$api, patientId, searchParams);

        // Update latest metric
        const latestHbA1c = healthService.getLatestHbA1c(rawData);
        if (latestHbA1c) {
          this.latestMetrics.hba1c = latestHbA1c.value;
          this.latestMetrics.hba1cUnit = latestHbA1c.unit;
          this.latestMetrics.hba1cType = latestHbA1c.type;
          this.latestMetrics.hba1cDate = latestHbA1c.date;
          // Tính trend tăng/giảm
          this.latestMetrics.hba1cTrend = this.calculateTrendIndicator(rawData, 'hba1c');
        }

        // ✅ Tính indicator thay đổi
        const hba1cChange = healthService.getHbA1cChange(rawData);
        this.metricsChange.hba1c = hba1cChange;
      } catch (error) {
        console.error('❌ Error loading HbA1C data:', error);
      }
    },

    async loadBmiData() {
      try {
        const patientId = this.userInformation.patientId;
        const periodFilterType = 3; // Default 30 days

        const currentDateTime = Math.floor(Date.now() / 1000);
        const fromDate = currentDateTime - (30 * 24 * 60 * 60);

        const searchParams = {
          currentDateTime: currentDateTime,
          periodFilterType: periodFilterType,
          fromDate: fromDate,
          toDate: currentDateTime,
          takeAll: true,
          trendType: periodFilterType,
          patientId: patientId
        };

        const rawData = await healthService.getBmiTrend(this.$api, patientId, searchParams);

        // Update latest metrics
        const latestBmiWeight = healthService.getLatestBmiWeight(rawData);
        if (latestBmiWeight) {
          this.latestMetrics.bmi = latestBmiWeight.bmi;
          this.latestMetrics.weight = latestBmiWeight.weight;
          this.latestMetrics.weightUnit = latestBmiWeight.weightUnit;
          this.latestMetrics.bmiUnit = latestBmiWeight.bmiUnit;
          this.latestMetrics.bmiType = latestBmiWeight.bmiType;
          this.latestMetrics.weightDate = latestBmiWeight.date;
        }

        // ✅ Lấy Waist riêng (THÊM ĐOẠN NÀY)
        const latestWaist = healthService.getLatestWaist(rawData);
        if (latestWaist) {
          this.latestMetrics.waist = latestWaist.waist;
          this.latestMetrics.waistUnit = latestWaist.waistUnit;
          this.latestMetrics.waistDate = latestWaist.date;
        }

        // ✅ Calculate weight trend
        this.latestMetrics.weightTrend = this.calculateTrendIndicator(rawData, 'weight');

        // ✅ Calculate waist trend
        this.latestMetrics.waistTrend = this.calculateTrendIndicator(rawData, 'waist');
      } catch (error) {
        console.error('❌ Error loading BMI data:', error);
      }
    },

    getTimeRangeOptions() {
      return [
        { value: 1, label: '7 ngày' },
        { value: 2, label: '14 ngày' },
        { value: 3, label: '30 ngày' },
        { value: 4, label: '90 ngày' }
      ];
    },

    async onChartFilterChange(chartType, value) {
      if (chartType === 'glucose') {
        this.glucoseChart.periodFilterType = value;
        await this.loadGlucoseData();
      } else if (chartType === 'bloodPressure') {
        this.bloodPressureChart.periodFilterType = value;
        await this.loadBloodPressureData();
      }
    },

    // ✅ 2. BLOOD PRESSURE CHART - Fixed X-Axis Date Format
    getBloodPressureChartOptions() {
      if (!this.bloodPressureChart.data) return {};

      let totalDataPoints = 0;
      if (this.bloodPressureChart.data.series) {
        this.bloodPressureChart.data.series.forEach(series => {
          if (series.data && series.data.length > totalDataPoints) {
            totalDataPoints = series.data.length;
          }
        });
      }

      let allValues = [];
      if (this.bloodPressureChart.data.series) {
        this.bloodPressureChart.data.series.forEach(series => {
          series.data.forEach(point => {
            if (point.y !== null && point.y !== undefined) {
              allValues.push(point.y);
            }
          });
        });
      }

      const minValue = allValues.length > 0 ? Math.min(...allValues) : 60;
      const maxValue = allValues.length > 0 ? Math.max(...allValues) : 180;

      const yAxisMin = Math.max(0, Math.floor(minValue - 20));
      const yAxisMax = Math.ceil(maxValue + 100);

      const periodFilterType = this.bloodPressureChart.periodFilterType;
      let tickAmount = 7;

      switch (periodFilterType) {
        case 1: tickAmount = 7; break;
        case 2: tickAmount = 7; break;
        case 3: tickAmount = 10; break;
        case 4: tickAmount = 12; break;
      }

      // ✅ Store raw data reference for tooltip
      const rawData = this.bloodPressureChart.rawData || [];

      return {
        chart: {
          type: 'line',
          height: 300,
          toolbar: { show: false },
          zoom: { enabled: totalDataPoints > 30 },
          animations: {
            enabled: true,
            speed: 400,
            dynamicAnimation: {
              enabled: true,
              speed: 200
            }
          },
          redrawOnParentResize: false,
          events: {
            markerClick: (event, chartContext, { seriesIndex }) => {
              const seriesName = chartContext.w.config.series[seriesIndex]?.name;
              if (seriesName) {
                this.toggleBloodPressureSeries(seriesName);
              }
            }
          }
        },
        colors: this.bloodPressureChart.data.colors || ['#ef4444', '#3b82f6', '#10b981'],
        stroke: {
          width: 2,
          curve: 'smooth'
        },
        xaxis: {
          type: 'datetime',
          labels: {
            rotate: totalDataPoints > 15 ? -45 : 0,
            rotateAlways: false,
            hideOverlappingLabels: true,
            style: {
              fontSize: '11px',
              colors: '#6b7280'
            },
            formatter: function (value, timestamp) {
              if (!timestamp) return '';
              const date = new Date(timestamp);
              return date.toLocaleDateString('vi-VN', {
                day: '2-digit',
                month: '2-digit'
              });
            },
            datetimeUTC: false
          },
          tickAmount: tickAmount,
          tickPlacement: 'on'
        },
        yaxis: {
          min: yAxisMin,
          max: yAxisMax,
          tickAmount: 6,
          labels: {
            style: {
              fontSize: '12px',
              colors: '#6b7280'
            },
            formatter: (value) => {
              return `${Math.round(value)} ${this.bloodPressureChart.data.unit || 'mmHg'}`;
            }
          }
        },
        grid: {
          borderColor: '#e5e7eb',
          strokeDashArray: 4
        },
        legend: {
          show: false
        },
        tooltip: {
          enabled: true,
          shared: true,
          intersect: false,
          followCursor: true,
          custom: ({ seriesIndex, dataPointIndex, w }) => {
            const timestamp = w.globals.seriesX[seriesIndex][dataPointIndex];
            const date = new Date(timestamp);

            // ✅ Find matching raw data entry - use raw data for all values to handle hidden series
            const matchingData = rawData.find(item => {
              const itemTime = new Date(item.date * 1000).getTime();
              return Math.abs(itemTime - timestamp) < 1000;
            });

            // ✅ Get values from raw data instead of series (handles hidden series case)
            const systolicValue = matchingData?.systolic || null;
            const diastolicValue = matchingData?.diastolic || null;
            const pulse = matchingData?.pulseRate || null;

            let html = `
          <div style="background: white; padding: 12px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); min-width: 200px;">
            <div style="font-size: 11px; color: #6b7280; margin-bottom: 8px;">
              ${date.toLocaleDateString('vi-VN')} ${date.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
            </div>
        `;

            // Systolic
            if (systolicValue !== null) {
              html += `
            <div style="margin-bottom: 6px;">
              <div style="display: flex; align-items: center; gap: 6px;">
                <div style="width: 8px; height: 8px; border-radius: 50%; background: #ef4444;"></div>
                <span style="font-size: 12px; color: #374151; font-weight: 500;">Tâm thu</span>
              </div>
              <div style="font-size: 18px; font-weight: 700; color: #111827; margin-left: 14px;">
                ${systolicValue} mmHg
              </div>
            </div>
          `;
            }

            // Diastolic
            if (diastolicValue !== null) {
              html += `
            <div style="margin-bottom: 6px;">
              <div style="display: flex; align-items: center; gap: 6px;">
                <div style="width: 8px; height: 8px; border-radius: 50%; background: #3b82f6;"></div>
                <span style="font-size: 12px; color: #374151; font-weight: 500;">Tâm trương</span>
              </div>
              <div style="font-size: 18px; font-weight: 700; color: #111827; margin-left: 14px;">
                ${diastolicValue} mmHg
              </div>
            </div>
          `;
            }

            // ✅ Pulse (if available)
            if (pulse) {
              html += `
            <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb;">
              <div style="display: flex; align-items: center; gap: 6px;">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="#10b981">
                  <path d="M2 6h2l1 3 2-6 1 3h2" stroke="#10b981" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span style="font-size: 11px; color: #10b981;">Nhịp tim</span>
              </div>
              <div style="font-size: 16px; font-weight: 600; color: #10b981; margin-left: 18px;">
                ${pulse} bpm
              </div>
            </div>
          `;
            }

            html += `</div>`;
            return html;
          }
        },
        markers: {
          size: totalDataPoints > 30 ? 4 : 6,
          hover: {
            size: totalDataPoints > 30 ? 8 : 10
          }
        }
      };
    },

    // ✅ 3. Glucose Series (không đổi)
    getGlucoseChartSeries() {
      if (!this.glucoseChart.data || !this.glucoseChart.data.series) return [];

      return this.glucoseChart.data.series.map(series => {
        const isHidden = this.glucoseChart.hiddenSeries.includes(series.name);
        return {
          ...series,
          data: isHidden ? [] : series.data,  
        };
      });
    },

    getBloodPressureChartSeries() {
      if (!this.bloodPressureChart.data || !this.bloodPressureChart.data.series) {
        return [];
      }

      return this.bloodPressureChart.data.series.map(s => {
        const isHidden = this.bloodPressureChart.hiddenSeries.includes(s.name);
        return {
          name: s.name,
          data: isHidden ? [] : s.data
            .filter(point => point.y !== null && point.y !== undefined)
            .sort((a, b) => a.x - b.x)
        };
      });
    },


    async handlePatientSelect(patient) {

      this.resetAllData();

      if (patient.tag?.toLowerCase() === 'diab' || patient.tag?.toLowerCase() === "both") {
        await this.handleSelectCustomer(patient);
      } else {
        await this.fetchHISPatientByPhone(patient);
      }

      this.savePatientToStorage(patient);
    },

    resetAllData() {
      // Reset latestMetrics
      this.latestMetrics = {
        glucose: null,
        glucoseUnit: 'mg/dL',
        glucoseType: '',
        glucoseDate: null,
        glucoseTrend: null,
        systolic: null,
        diastolic: null,
        bpUnit: 'mmHg',
        bloodPressureType: '',
        bloodPressureDate: null,
        bloodPressureTrend: null,
        weight: null,
        weightUnit: 'kg',
        weightType: '',
        weightDate: null,
        weightTrend: null,
        waist: null,
        waistUnit: 'cm',
        waistType: '',
        waistDate: null,
        waistTrend: null,
        hba1c: null,
        hba1cUnit: '%',
        hba1cType: '',
        hba1cDate: null,
        hba1cTrend: null,
        bmi: null,
        bmiUnit: '',
        bmiType: '',
        liver: null,
        liverUnit: 'IU/L',
        kidney: null,
        kidneyUnit: 'mg/dL',
      };

      // Reset chart data
      this.glucoseChart.data = null;
      this.glucoseChart.rawData = null;
      this.bloodPressureChart.data = null;
      this.bloodPressureChart.rawData = null;

      // Reset lab results
      this.labResults = [];
      this.filteredLabResults = [];
      this.labResultsOriginal = [];

      // Reset medications & doctor notes
      this.medications = [];
      this.doctorNotes = [];

      // Reset statistics
      this.lastestMetricsNextBloodSugar = {
        latestExaminationDate: '',
        nextExaminationDate: '',
        unit: '',
        value: null,
      };
      this.lastestMetricsNextBloodPressure = {
        latestExaminationDate: '',
        nextExaminationDate: '',
        unit: '',
        value: null,
      };

      console.log('✅ All data reset');
    },

    /**
     * Lưu patient vào localStorage
     */
    savePatientToStorage(patient) {
      try {
        const storageData = {
          patient: patient,
          userInformation: this.userInformation,
          timestamp: Date.now(),
        };
        localStorage.setItem('dashboard_his_selected_patient', JSON.stringify(storageData));
        console.log('✅ Patient saved to localStorage');
      } catch (error) {
        console.error('❌ Error saving to localStorage:', error);
      }
    },

    /**
     * Load patient từ localStorage
     */
    loadPatientFromStorage() {
      try {
        const stored = localStorage.getItem('dashboard_his_selected_patient');
        if (stored) {
          const data = JSON.parse(stored);
          // Kiểm tra data không quá 24h
          const hoursDiff = (Date.now() - data.timestamp) / (1000 * 60 * 60);
          if (hoursDiff < 24 && data.patient) {
            console.log('✅ Loading patient from localStorage');
            return data.patient;
          }
        }
        return null;
      } catch (error) {
        console.error('❌ Error loading from localStorage:', error);
        return null;
      }
    },

    /**
     * Xóa patient khỏi localStorage
     */
    clearPatientStorage() {
      localStorage.removeItem('dashboard_his_selected_patient');
      console.log('✅ Patient cleared from localStorage');
    },

    async fetchHISPatientByPhone(patient) {

      if (!patient) {
        this.$toastr.e({
          title: 'Lỗi',
          msg: 'Không tìm thấy thông tin bệnh nhân',
        });
        return;
      }

      // Nếu không có phoneNumber, kiểm tra xem có idNumber không
      const idNumber = patient.idNumber || patient.id;
      if (!patient.phoneNumber && !idNumber) {
        this.$toastr.e({
          title: 'Lỗi',
          msg: 'Không tìm thấy số điện thoại hoặc mã bệnh nhân',
        });
        return;
      }

      this.$store.commit('context/setLoading', true);

      try {
        let hisPatients;

        if (patient.phoneNumber) {
          // Tìm theo số điện thoại
          const phone = patient.phoneNumber;
          hisPatients = await hisLabService.searchByPhone(phone);
        } else {
          // Tìm theo idNumber
          hisPatients = await hisLabService.searchByPatientId(idNumber);
        }

        if (hisPatients && hisPatients.length > 0) {
          // HIS API trả về array, lấy phần tử đầu tiên
          const hisPatient = hisPatients[0];
          // Lấy phone từ HIS response hoặc patient input
          const phoneNumber = hisPatient.phoneNumber || patient.phoneNumber || '';
          const formattedPhone = hisLabService.formatPhoneNumber(phoneNumber);

          this.userInformation = {
            patientId: hisPatient.patientId || patient.id,
            idNumber: hisPatient.idNumber || patient.idNumber || hisPatient.patientId || patient.id,
            accountId: hisPatient.accountId || null,
            name: hisPatient.fullName || patient.fullName,
            phoneNumber: formattedPhone,
            birthday: hisPatient.dateOfBirth || patient.dateOfBirth,
            gender: hisPatient.gender === 'F' ? 'Nữ' : hisPatient.gender === 'M' ? 'Nam' : hisPatient.gender,
            typeOfSick: hisPatient.typeOfSick || patient.typeOfSick || 'Chưa xác định',
            age: hisPatient.age || patient.age,
            tag: patient.tag || 'his', // ✅ Lưu tag
            source: 'HIS',
            isCustomerReceive: patient.isCustomerReceive ?? false, // ✅ HIS mặc định false
            zaloGroupId: hisPatient.zaloGroupId || patient.zaloGroupId || '',
          };

          // ✅ Map HIS metrics data vào latestMetrics
          this.mapHISDataToMetrics(hisPatient);

          this.$bvModal.hide('customer-select-modal');

          // Load thêm data khác cho HIS patient
          await Promise.all([
            this.loadLabResults(),
            this.loadMedications(),
            this.loadDoctorNotes(),
            this.loadLabResultsAlt(),
            this.loadLabResultsCreatinine(),
            this.loadBloodPressureVitalSigns(),
            this.loadGlucoseVitalSigns(),
          ]);
        } else {
          this.$toastr.e({
            title: 'Lỗi',
            msg: 'Không tìm thấy thông tin bệnh nhân từ HIS',
          });
        }
      } catch (error) {
        console.error('Error fetching HIS patient:', error);
        this.$toastr.e({
          title: 'Lỗi',
          msg: 'Không thể tải thông tin bệnh nhân từ HIS',
        });
      } finally {
        this.$store.commit('context/setLoading', false);
      }
    },

    /**
     * Map HIS API data vào latestMetrics
     * HIS data structure:
     * - listGlucose: [{ value, date, unit, status }]
     * - listBloodPressure: [{ value: "80/90", date, unit, status }]
     * - listWeight: [{ value, date, waist, unit, status }]
     * - listHba1c: [{ value, date, unit, status }]
     */
    mapHISDataToMetrics(hisPatient) {
      // ✅ Map Glucose
      if (hisPatient.listGlucose && hisPatient.listGlucose.length > 0) {
        const sortedGlucose = [...hisPatient.listGlucose].sort((a, b) => b.date - a.date);
        const latest = sortedGlucose[0];
        this.latestMetrics.glucose = latest.value;
        this.latestMetrics.glucoseUnit = latest.unit || 'mg/dL';
        this.latestMetrics.glucoseType = this.getGlucoseTypeFromValue(latest.value);
        this.latestMetrics.glucoseDate = latest.date;

        // Calculate trend
        if (sortedGlucose.length >= 2) {
          const diff = sortedGlucose[0].value - sortedGlucose[1].value;
          const diffRounded = Math.round(Math.abs(diff) * 10) / 10;
          if (diff > 0) {
            this.latestMetrics.glucoseTrend = { direction: 'up', diff: diffRounded };
          } else if (diff < 0) {
            this.latestMetrics.glucoseTrend = { direction: 'down', diff: diffRounded };
          } else {
            this.latestMetrics.glucoseTrend = { direction: 'stable', diff: 0 };
          }
        }
      }

      // ✅ Map Blood Pressure
      if (hisPatient.listBloodPressure && hisPatient.listBloodPressure.length > 0) {
        const sortedBP = [...hisPatient.listBloodPressure].sort((a, b) => b.date - a.date);
        const latest = sortedBP[0];

        // Parse "80/90" format
        if (latest.value && typeof latest.value === 'string' && latest.value.includes('/')) {
          const parts = latest.value.split('/');
          this.latestMetrics.systolic = parseInt(parts[0]) || null;
          this.latestMetrics.diastolic = parseInt(parts[1]) || null;
        }
        this.latestMetrics.bpUnit = latest.unit || 'mmHg';
        this.latestMetrics.bloodPressureType = latest.status || this.getBloodPressureTypeFromValue(this.latestMetrics.systolic, this.latestMetrics.diastolic);
        this.latestMetrics.bloodPressureDate = latest.date;

        // Calculate trend (dựa trên systolic)
        if (sortedBP.length >= 2) {
          const latestSystolic = parseInt(sortedBP[0].value?.split('/')[0]) || 0;
          const prevSystolic = parseInt(sortedBP[1].value?.split('/')[0]) || 0;
          const diff = latestSystolic - prevSystolic;
          const diffRounded = Math.round(Math.abs(diff) * 10) / 10;
          if (diff > 0) {
            this.latestMetrics.bloodPressureTrend = { direction: 'up', diff: diffRounded };
          } else if (diff < 0) {
            this.latestMetrics.bloodPressureTrend = { direction: 'down', diff: diffRounded };
          } else {
            this.latestMetrics.bloodPressureTrend = { direction: 'stable', diff: 0 };
          }
        }
      }

      // ✅ Map Weight & Waist
      if (hisPatient.listWeight && hisPatient.listWeight.length > 0) {
        const sortedWeight = [...hisPatient.listWeight].sort((a, b) => b.date - a.date);
        const latest = sortedWeight[0];

        this.latestMetrics.weight = latest.value;
        this.latestMetrics.weightUnit = latest.unit || 'kg';
        this.latestMetrics.weightDate = latest.date;
        this.latestMetrics.bmiType = latest.status || 'Đã cập nhật';

        // Waist from same record
        if (latest.waist) {
          this.latestMetrics.waist = latest.waist;
          this.latestMetrics.waistUnit = 'cm';
          this.latestMetrics.waistDate = latest.date;
        }

        // Calculate weight trend
        if (sortedWeight.length >= 2) {
          const diff = sortedWeight[0].value - sortedWeight[1].value;
          const diffRounded = Math.round(Math.abs(diff) * 10) / 10;
          if (diff > 0) {
            this.latestMetrics.weightTrend = { direction: 'up', diff: diffRounded };
          } else if (diff < 0) {
            this.latestMetrics.weightTrend = { direction: 'down', diff: diffRounded };
          } else {
            this.latestMetrics.weightTrend = { direction: 'stable', diff: 0 };
          }
        }

        // Calculate waist trend
        const waistRecords = sortedWeight.filter(r => r.waist);
        if (waistRecords.length >= 2) {
          const diff = waistRecords[0].waist - waistRecords[1].waist;
          const diffRounded = Math.round(Math.abs(diff) * 10) / 10;
          if (diff > 0) {
            this.latestMetrics.waistTrend = { direction: 'up', diff: diffRounded };
          } else if (diff < 0) {
            this.latestMetrics.waistTrend = { direction: 'down', diff: diffRounded };
          } else {
            this.latestMetrics.waistTrend = { direction: 'stable', diff: 0 };
          }
        }
      }

      // ✅ Map HbA1c
      if (hisPatient.listHba1c && hisPatient.listHba1c.length > 0) {
        const sortedHba1c = [...hisPatient.listHba1c].sort((a, b) => b.date - a.date);
        const latest = sortedHba1c[0];

        this.latestMetrics.hba1c = latest.value;
        this.latestMetrics.hba1cUnit = latest.unit || '%';
        this.latestMetrics.hba1cType = this.getHbA1cTypeFromValue(latest.value);
        this.latestMetrics.hba1cDate = latest.date;

        // Calculate trend
        if (sortedHba1c.length >= 2) {
          const diff = sortedHba1c[0].value - sortedHba1c[1].value;
          const diffRounded = Math.round(Math.abs(diff) * 10) / 10;
          if (diff > 0) {
            this.latestMetrics.hba1cTrend = { direction: 'up', diff: diffRounded };
          } else if (diff < 0) {
            this.latestMetrics.hba1cTrend = { direction: 'down', diff: diffRounded };
          } else {
            this.latestMetrics.hba1cTrend = { direction: 'stable', diff: 0 };
          }
        }
      }

      console.log('✅ HIS data mapped to latestMetrics:', this.latestMetrics);
    },

    // Helper: Xác định loại glucose từ giá trị
    getGlucoseTypeFromValue(value) {
      if (!value) return '';
      if (value < 70) return 'Thấp';
      if (value <= 100) return 'Bình thường';
      if (value <= 125) return 'Tiền ĐTĐ';
      return 'Cao';
    },

    // Helper: Xác định loại huyết áp từ giá trị
    getBloodPressureTypeFromValue(systolic, diastolic) {
      if (!systolic || !diastolic) return '';
      if (systolic < 120 && diastolic < 80) return 'Bình thường';
      if (systolic < 130 && diastolic < 80) return 'Tăng nhẹ';
      if (systolic < 140 || diastolic < 90) return 'Tăng HA độ 1';
      if (systolic >= 140 || diastolic >= 90) return 'Tăng HA độ 2';
      return 'Cao';
    },

    // Helper: Xác định loại HbA1c từ giá trị
    getHbA1cTypeFromValue(value) {
      if (!value) return '';
      if (value < 5.7) return 'Bình thường';
      if (value < 6.5) return 'Tiền ĐTĐ';
      return 'Cao';
    },

    async handleSelectCustomer(customer) {
      const patientId = customer.patientId || customer.id;

      if (!customer || !patientId) {
        this.$toastr.e({
          title: 'Lỗi',
          msg: 'Không tìm thấy ID bệnh nhân',
        }

        );
        return;
      }

      // Update user information
      this.userInformation = {
        patientId: patientId,
        accountId: customer.accountId,
        name: customer.name || customer.fullName,
        phoneNumber: customer.phoneNumber || customer.phone,
        birthday: customer.birthday || customer.dateOfBirth,
        gender: customer.gender,
        typeOfSick: customer.typeOfSick || customer.diagnosisType,
        age: customer.age,
        tag: customer.tag || 'diab', // ✅ Lưu tag
        source: 'DIAB',
        isCustomerReceive: customer.isCustomerReceive ?? true, // ✅ DiaB mặc định true
        zaloGroupId: customer.zaloGroupId || '',
      };

      // Update patient data if available
      if (customer.riskLevel) {
        this.patientData.riskLevel = customer.riskLevel;
      }
      if (customer.alerts !== undefined) {
        this.patientData.alerts = customer.alerts;
      }
      if (customer.nextAppointment) {
        this.patientData.nextAppointment = customer.nextAppointment;
      }
      this.$bvModal.hide('customer-select-modal');


      await Promise.all([
        this.loadUserData(),
        this.loadLabResults(),
        this.loadMedications(),
        this.loadDoctorNotes(),
        this.loadLabResultsAlt(),
        this.loadLabResultsCreatinine(),
        this.loadBloodPressureVitalSigns(),
        this.loadGlucoseVitalSigns(),
      ]);
    },

    formatDate(date) {
      if (!date) return '';

      if (typeof date === 'number') {
        const plus7hTimestamp = date + 7 * 60 * 60;
        return convertTimestampToDateTime(plus7hTimestamp);
      }

      const d = new Date(date);
      d.setHours(d.getHours() + 7);
      return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
    },


    formatYear(timestamp) {
      return convertTimestampToYear(timestamp);
    },

    formatDateTime(timestamp) {
      return formatDateTimeWithTime(timestamp);
    },

    // Format date với thời gian dd/mm/yyyy HH:mm
    formatDateWithTime(date) {
      if (!date) return '--';

      try {
        const d = new Date(date);
        if (isNaN(d.getTime())) return '--';

        const day = d.getDate().toString().padStart(2, '0');
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const year = d.getFullYear();
        const hours = d.getHours().toString().padStart(2, '0');
        const minutes = d.getMinutes().toString().padStart(2, '0');

        return `${day}/${month}/${year} ${hours}:${minutes}`;
      } catch (error) {
        return '--';
      }
    },

    getRiskClass(riskLevel) {
      if (!riskLevel) return 'medium';
      const level = riskLevel.toLowerCase();
      if (level.includes('high')) return 'high';
      if (level.includes('medium')) return 'medium';
      return 'low';
    },

    getGlucoseStatus(glucoseType) {
      if (!glucoseType) return '';
      switch (glucoseType.toLowerCase()) {
        case 'cao':
        case 'rất cao':
          return 'danger';
        case 'tốt':
          return 'success';
        case 'thấp':
        case 'rất thấp':
          return 'warning';
        default:
          return 'danger';
      }
    },

    getBloodPressureStatus(bloodPressureType) {
      if (!bloodPressureType) return '';

      const typeMap = {
        'Thấp': 'danger',                      // Low
        'Bình thường': 'success',              // Normal
        'Bình thường cao': 'info',             // PreIncrease
        'Tăng huyết áp độ 1': 'warning',       // IncreaseLevelOne
        'Tăng huyết áp độ 2': 'danger',        // IncreaseLevelTwo
        'Tăng huyết áp độ 3': 'critical',      // IncreaseLevelThree
        'không rõ': 'none'                     // None
      };

      return typeMap[bloodPressureType] || '';
    },

    getBloodPressureStatusText(bloodPressureType) {
      // Trả về text như backend gửi, không cần convert
      return bloodPressureType || 'Không rõ';
    },

    getHbA1cStatus(hba1cType) {
      if (!hba1cType) return '';
      switch (hba1cType.toLowerCase()) {
        case 'lý tưởng':
        case 'tốt':
          return 'success';
        case 'cao':
        case 'rất cao':
          return 'danger';
        default:
          return 'warning';
      }
    },


    getWeightStatus(weightType) {
      if (!weightType) return '';

      switch (weightType.toLowerCase()) {
        case 'lý tưởng':
          return 'success';

        case 'thiếu cân':
          return 'warning';

        case 'thừa cân':
          return 'danger';

        default:
          return 'warning';
      }
    },



    // BMI status helpers
    // getBmiStatus(bmi) {
    //   if (!bmi) return '';
    //   if (bmi < 18.5) return 'warning';
    //   if (bmi >= 18.5 && bmi < 25) return 'success';
    //   if (bmi >= 25 && bmi < 30) return 'warning';
    //   return 'danger';
    // },

    // getBmiStatusText(bmi) {
    //   if (!bmi) return 'Chưa có dữ liệu';
    //   if (bmi < 18.5) return 'Thiếu cân';
    //   if (bmi >= 18.5 && bmi < 25) return 'Bình thường';
    //   if (bmi >= 25 && bmi < 30) return 'Thừa cân';
    //   return 'Béo phì';
    // },

    getLiverStatus(alt) {
      if (!alt) return '';
      if (alt <= 45) return 'success'; // Normal
      if (alt > 45 && alt <= 100) return 'warning'; // Elevated
      return 'danger'; // High
    },

    getLiverStatusText(alt) {
      if (!alt) return 'Chưa có dữ liệu';
      if (alt <= 45) return 'Bình thường';
      if (alt > 45 && alt <= 100) return 'Cao nhẹ';
      return 'Cao';
    },

    getKidneyStatus(creatinine) {
      if (!creatinine) return '';
      // Normal range: 0.6 - 1.2 mg/dL
      if (creatinine >= 0.6 && creatinine <= 1.2) return 'success'; // Normal
      if (creatinine > 1.2 && creatinine <= 2.0) return 'warning'; // Elevated
      return 'danger'; // High
    },

    getKidneyStatusText(creatinine) {
      if (!creatinine) return 'Chưa có dữ liệu';
      if (creatinine >= 0.6 && creatinine <= 1.2) return 'Bình thường';
      if (creatinine < 0.6) return 'Thấp';
      if (creatinine > 1.2 && creatinine <= 2.0) return 'Cao nhẹ';
      return 'Cao';
    },
    // Trong Vue component
    async loadLabResults() {
      this.labResultsLoading = true;

      try {
        const patientParams = {
          phone: this.userInformation.phoneNumber,
          idNumber: this.userInformation.idNumber
        };
        const [mainResults, altResults, creatinineResults] = await Promise.all([
          hisLabService.getLabResults(patientParams),
          hisLabService.getLabResultsAlt(patientParams),
          hisLabService.getLabResultsCreatinine(patientParams)
        ]);

        const allRawData = [...mainResults, ...altResults, ...creatinineResults];
        const formattedResults = hisLabService.formatLabResultsToTable(allRawData);

        // ✅ Remove duplicates
        const uniqueResults = formattedResults.filter((result, index, self) =>
          index === self.findIndex((r) => (
            r.testName === result.testName &&
            r.dateISO === result.dateISO
          ))
        );

        // ✅ Calculate status
        this.labResultsOriginal = uniqueResults.map(result => {
          const calculatedStatus = this.calculateLabStatus(result.value, result.range);
          return {
            ...result,
            status: calculatedStatus.status,
            statusText: calculatedStatus.statusText
          };
        });

        this.labResults = [...this.labResultsOriginal];
        this.filteredLabResults = [...this.labResultsOriginal];

        // ✅ Update metrics
        const latestAlt = hisLabService.getLatestAlt(altResults);
        if (latestAlt) {
          this.latestMetrics.liver = latestAlt.value;
          this.latestMetrics.liverUnit = latestAlt.unit;
          this.latestMetrics.liverDate = latestAlt.date;
        }

        const latestCreatinine = hisLabService.getLatestCreatinine(creatinineResults);
        if (latestCreatinine) {
          this.latestMetrics.kidney = latestCreatinine.value;
          this.latestMetrics.kidneyUnit = latestCreatinine.unit;
          this.latestMetrics.kidneyDate = latestCreatinine.date;
        }

      } catch (error) {
        console.error('❌ Error:', error);
      } finally {
        this.labResultsLoading = false;
      }
    },

    filterLabResults() {
      console.log('🔍 Starting filter with:', {
        monthFilter: this.labResultsFilters.monthFilter,
        statusFilter: this.labResultsFilters.statusFilter,
        categoryFilter: this.labResultsFilters.categoryFilter,
        totalResults: this.labResultsOriginal.length
      });

      let results = [...this.labResultsOriginal];

      // 1. Filter by search text
      if (this.labResultsSearch && this.labResultsSearch.trim()) {
        const searchLower = this.labResultsSearch.toLowerCase().trim();
        results = results.filter(result => {
          return (
            (result.testName && result.testName.toLowerCase().includes(searchLower)) ||
            (result.category && result.category.toLowerCase().includes(searchLower)) ||
            (result.value && result.value.toString().toLowerCase().includes(searchLower))
          );
        });
        console.log('✅ After search filter:', results.length);
      }

      // 2. Filter by month range
      if (this.labResultsFilters.monthFilter) {
        // Lấy value từ object hoặc trực tiếp
        const filter = typeof this.labResultsFilters.monthFilter === 'object' && this.labResultsFilters.monthFilter.value
          ? this.labResultsFilters.monthFilter.value
          : this.labResultsFilters.monthFilter;

        if (filter && filter.type === 'range' && filter.months) {
          const now = new Date();
          const cutoffDate = new Date();
          cutoffDate.setMonth(now.getMonth() - filter.months);

          console.log('📅 Month filter:', {
            months: filter.months,
            cutoffDate: cutoffDate.toISOString(),
            now: now.toISOString()
          });

          results = results.filter(result => {
            // ✅ Validate timestamp exists
            if (!result.dateTimestamp || isNaN(result.dateTimestamp)) {
              console.warn('⚠️ Invalid timestamp for result:', result.date, result);
              return false;
            }

            const resultTimestamp = result.dateTimestamp;
            const cutoffTimestamp = cutoffDate.getTime();
            const nowTimestamp = now.getTime();

            const isInRange = resultTimestamp >= cutoffTimestamp && resultTimestamp <= nowTimestamp;

            return isInRange;
          });

          console.log('✅ After month filter:', results.length);
        }
      }

      // 3. Filter by status
      if (this.labResultsFilters.statusFilter) {
        // Lấy value từ object hoặc string
        const statusValue = typeof this.labResultsFilters.statusFilter === 'object'
          ? this.labResultsFilters.statusFilter.value
          : this.labResultsFilters.statusFilter;

        console.log('🎯 Status filter:', statusValue);
        console.log('📊 Available statuses:', [...new Set(results.map(r => r.status))]);

        results = results.filter(result => {
          if (!result.status) {
            console.warn('⚠️ Result missing status:', result);
            return false;
          }
          return result.status.toLowerCase() === statusValue.toLowerCase();
        });

        console.log('✅ After status filter:', results.length);
      }

      // 4. Filter by category
      if (this.labResultsFilters.categoryFilter) {
        // Lấy value từ object hoặc string
        const categoryValue = typeof this.labResultsFilters.categoryFilter === 'object'
          ? this.labResultsFilters.categoryFilter.value
          : this.labResultsFilters.categoryFilter;

        console.log('📂 Category filter:', categoryValue);
        console.log('📊 Available categories:', [...new Set(results.map(r => r.category))]);

        results = results.filter(result => {
          if (!result.category) {
            console.warn('⚠️ Result missing category:', result);
            return false;
          }
          return result.category.trim() === categoryValue.trim();
        });

        console.log('✅ After category filter:', results.length);
      }

      console.log('🎉 Final filtered results:', results.length);
      this.filteredLabResults = results;
      this.currentPage = 1;
    },

    getMonthOptions() {
      return [
        {
          value: { type: 'range', months: 3 },
          label: '3 tháng gần nhất'
        },
        {
          value: { type: 'range', months: 6 },
          label: '6 tháng gần nhất'
        },
        {
          value: { type: 'range', months: 12 },
          label: '12 tháng gần nhất'
        },
        {
          value: { type: 'range', months: 24 },
          label: '24 tháng gần nhất'
        }
      ];
    },

    // Get status options - khớp với calculateLabStatus()
    getStatusOptions() {
      return [
        { value: 'danger', label: 'Cao' },
        { value: 'warning', label: 'Thấp' },
        { value: 'success', label: 'Bình thường' },
        { value: 'info', label: 'Đang cập nhật' }
      ];
    },

    // Get category options from existing data
    getCategoryOptions() {
      if (!this.labResultsOriginal.length) return [];

      const categories = [...new Set(this.labResultsOriginal.map(r => r.category))];
      return categories.map(cat => ({
        value: cat,
        label: cat
      }));
    },

    // Download lab results
    async downloadLabResults() {
      try {
        const { jsPDF } = await import('jspdf');
        await import('jspdf-autotable');

        const doc = new jsPDF({
          orientation: 'landscape',
          unit: 'pt',
          format: 'a4'
        });

        // ✅ Use Helvetica which supports Vietnamese better than Times
        doc.setFont('helvetica');

        // Add title
        doc.setFontSize(18);
        doc.text('KET QUA XET NGHIEM CHI TIET', doc.internal.pageSize.getWidth() / 2, 40, { align: 'center' });

        // Add patient info
        doc.setFontSize(11);
        doc.text(`Benh nhan: ${this.userInformation.name || 'N/A'}`, 40, 70);
        doc.text(`Ngay xuat: ${new Date().toLocaleDateString('vi-VN')}`, 40, 85);
        doc.text(`Tong so ket qua: ${this.filteredLabResults.length}`, 40, 100);

        // Prepare table data
        const headers = [
          ['Ngày', 'Nhóm', 'Y lệnh', 'Kết quả', 'Đơn vị', 'Khoảng tham chiếu', 'Đánh giá']
        ];

        const rows = this.filteredLabResults.map(result => [
          result.date,
          result.category,
          result.testName,
          result.value,
          result.unit,
          result.range.includes('null') ? 'Dang cap nhat' : result.range,
          result.statusText
        ]);

        // Add table
        doc.autoTable({
          head: headers,
          body: rows,
          startY: 110,
          styles: {
            font: 'helvetica',
            fontSize: 9,
            cellPadding: 6,
            overflow: 'linebreak',
            halign: 'left',
            valign: 'middle'
          },
          headStyles: {
            fillColor: [1, 100, 90],
            textColor: 255,
            fontStyle: 'bold',
            halign: 'center'
          },
          columnStyles: {
            0: { cellWidth: 70 },
            1: { cellWidth: 80 },
            2: { cellWidth: 150 },
            3: { cellWidth: 60, halign: 'center' },
            4: { cellWidth: 60, halign: 'center' },
            5: { cellWidth: 80, halign: 'center' },
            6: { cellWidth: 90, halign: 'center' }
          },
          didParseCell: function (data) {
            // Color code status column
            if (data.column.index === 6 && data.section === 'body') {
              const statusText = data.cell.raw;
              if (statusText === 'Cao bat thuong' || statusText.includes('bat thuong')) {
                data.cell.styles.textColor = [220, 38, 38];
                data.cell.styles.fontStyle = 'bold';
              } else if (statusText === 'Cao' || statusText === 'Thap') {
                data.cell.styles.textColor = [217, 119, 6];
                data.cell.styles.fontStyle = 'bold';
              } else if (statusText === 'Binh thuong') {
                data.cell.styles.textColor = [22, 163, 74];
                data.cell.styles.fontStyle = 'bold';
              }
            }

            // Color code value column
            if (data.column.index === 3 && data.section === 'body') {
              const rowData = this.filteredLabResults[data.row.index];
              if (rowData.status === 'danger') {
                data.cell.styles.textColor = [220, 38, 38];
                data.cell.styles.fontStyle = 'bold';
              } else if (rowData.status === 'warning') {
                data.cell.styles.textColor = [217, 119, 6];
              }
            }
          }.bind(this),
          margin: { top: 110, left: 40, right: 40 },
        });

        // Add footer
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.text(
            `Trang ${i} / ${pageCount}`,
            doc.internal.pageSize.getWidth() / 2,
            doc.internal.pageSize.getHeight() - 20,
            { align: 'center' }
          );
        }

        // Save PDF
        const fileName = `ket-qua-xet-nghiem-${this.userInformation.name || 'benh-nhan'}-${new Date().getTime()}.pdf`;
        doc.save(fileName);

        this.$toastr.s({
          title: 'Thành công',
          msg: 'Đã tải xuống thành công'
        });

      } catch (error) {
        this.$toastr.e({
          title: 'Lỗi',
          msg: 'Đã có lỗi xảy ra'
        });
      }
    },



    async loadDoctorNotes() {
      this.doctorNotesLoading = true;

      try {
        const rawData = await hisLabService.getDoctorNotes({
          phone: this.userInformation.phoneNumber,
          idNumber: this.userInformation.idNumber
        });

        this.doctorNotes = hisLabService.formatDoctorNotes(rawData);

      } catch (error) {
        console.error('Lỗi load doctor notes:', error);
        this.$bvToast.toast('Không thể tải ghi chú bác sĩ', {
          title: 'Lỗi',
          variant: 'danger'
        });
      } finally {
        this.doctorNotesLoading = false;
      }
    },
    async loadMedications() {
      this.medicationsLoading = true;

      try {
        const rawData = await hisLabService.getActiveMedications({
          phone: this.userInformation.phoneNumber,
          idNumber: this.userInformation.idNumber
        });

        this.medications = hisLabService.formatMedications(rawData);

        // Lấy thống kê
        const stats = hisLabService.getMedicationStatistics(rawData);
        // Hiển thị warning nếu có thuốc sắp hết hạn
        if (stats.expiringSoon > 0) {
          this.$bvToast.toast(
            `Có ${stats.expiringSoon} loại thuốc sắp hết hạn sử dụng`,
            {
              title: 'Cảnh báo',
              variant: 'warning',
              autoHideDelay: 5000
            }
          );
        }

      } catch (error) {
        console.error('Lỗi load medications:', error);
        this.$bvToast.toast('Không thể tải danh sách thuốc', {
          title: 'Lỗi',
          variant: 'danger'
        });
      } finally {
        this.medicationsLoading = false;
      }
    },
    async loadLabResultsAlt() {
      this.labResultsLoading = true;

      try {
        const rawData = await hisLabService.getLabResultsAlt({
          phone: this.userInformation.phoneNumber,
          idNumber: this.userInformation.idNumber
        });
        const formattedResults = hisLabService.formatLabResultsToTable(rawData);

        // ✅ Merge với labResults hiện tại và apply status calculation
        const mergedResults = [...this.labResultsOriginal];

        formattedResults.forEach(result => {
          const calculatedStatus = this.calculateLabStatus(result.value, result.range);
          const resultWithStatus = {
            ...result,
            status: calculatedStatus.status,
            statusText: calculatedStatus.statusText
          };

          // Thêm vào nếu chưa tồn tại
          const exists = mergedResults.find(r =>
            r.id === result.id ||
            (r.date === result.date && r.testName === result.testName)
          );

          if (!exists) {
            mergedResults.push(resultWithStatus);
          }
        });

        this.labResultsOriginal = mergedResults;
        this.labResults = [...this.labResultsOriginal];
        this.filteredLabResults = [...this.labResultsOriginal];

        // ✅ Map ALT resultValue → liver
        const latestLiver = hisLabService.getLatestAlt(rawData);
        if (latestLiver) {
          this.latestMetrics.liver = latestLiver.value;
          this.latestMetrics.liverUnit = latestLiver.unit || 'IU/L';
          this.latestMetrics.liverDate = latestLiver.date;
        }

      } catch (error) {
        console.error('Lỗi load lab results ALT:', error);
      } finally {
        this.labResultsLoading = false;
      }
    },

    async loadLabResultsCreatinine() {
      this.labResultsLoading = true;

      try {
        const rawData = await hisLabService.getLabResultsCreatinine({
          phone: this.userInformation.phoneNumber,
          idNumber: this.userInformation.idNumber
        });
        const formattedResults = hisLabService.formatLabResultsToTable(rawData);

        // ✅ Merge với labResults hiện tại và apply status calculation
        const mergedResults = [...this.labResultsOriginal];

        formattedResults.forEach(result => {
          const calculatedStatus = this.calculateLabStatus(result.value, result.range);
          const resultWithStatus = {
            ...result,
            status: calculatedStatus.status,
            statusText: calculatedStatus.statusText
          };

          // Thêm vào nếu chưa tồn tại
          const exists = mergedResults.find(r =>
            r.id === result.id ||
            (r.date === result.date && r.testName === result.testName)
          );

          if (!exists) {
            mergedResults.push(resultWithStatus);
          }
        });

        this.labResultsOriginal = mergedResults;
        this.labResults = [...this.labResultsOriginal];
        this.filteredLabResults = [...this.labResultsOriginal];

        // ✅ Map Creatinine resultValue → kidney
        const latestKidney = hisLabService.getLatestCreatinine(rawData);
        if (latestKidney) {
          this.latestMetrics.kidney = latestKidney.value;
          this.latestMetrics.kidneyUnit = latestKidney.unit || 'mg/dL';
          this.latestMetrics.kidneyDate = latestKidney.date;
        }

      } catch (error) {
        console.error('Lỗi load lab results Creatinine:', error);
      } finally {
        this.labResultsLoading = false;
      }
    },

    async loadGlucoseVitalSigns() {
      try {
        const rawData = await hisLabService.getVitalSignsByPhone(
          this.$api,
          { phone: this.userInformation.phoneNumber, idNumber: this.userInformation.idNumber },
          'glucose'
        );

        // ✅ Clean one-liner update
        this.lastestMetricsNextBloodSugar = {
          ...hisLabService.formatGlucoseVitalSigns(rawData)
        };

      } catch (error) {
        console.error('❌ Error loading glucose vital signs:', error);
      }
    },

    async loadBloodPressureVitalSigns() {
      try {
        const rawData = await hisLabService.getVitalSignsByPhone(
          this.$api,
          { phone: this.userInformation.phoneNumber, idNumber: this.userInformation.idNumber },
          'bloodpressure'
        );

        // ✅ Clean one-liner update
        this.lastestMetricsNextBloodPressure = {
          ...hisLabService.formatBloodPressureVitalSigns(rawData)
        };

      } catch (error) {
        console.error('❌ Error loading blood pressure vital signs:', error);
      }
    },

    /**
     * Tính trend indicator (Tăng/Giảm/Ổn định) từ 2 giá trị gần nhất
     * @param {Array} rawData - Dữ liệu từ API
     * @param {String} type - Loại metric: 'glucose', 'bloodPressure', 'hba1c', 'weight'
     */
    calculateTrendIndicator(rawData, type) {
      if (!rawData || !Array.isArray(rawData) || rawData.length < 2) {
        return null;
      }

      let sorted = [];
      let latestValue, previousValue;

      if (type === 'glucose') {
        sorted = [...rawData]
          .filter(x => x.glucose && x.createDate)
          .sort((a, b) => b.createDate - a.createDate);
        if (sorted.length < 2) return null;
        latestValue = sorted[0].glucose;
        previousValue = sorted[1].glucose;
      } else if (type === 'bloodPressure') {
        sorted = [...rawData]
          .filter(x => x.systolic && x.date)
          .sort((a, b) => b.date - a.date);
        if (sorted.length < 2) return null;
        latestValue = sorted[0].systolic;
        previousValue = sorted[1].systolic;
      } else if (type === 'hba1c') {
        sorted = [...rawData]
          .filter(x => x.hbA1C && x.date)
          .sort((a, b) => b.date - a.date);
        if (sorted.length < 2) return null;
        latestValue = sorted[0].hbA1C;
        previousValue = sorted[1].hbA1C;
      } else if (type === 'weight') {
        sorted = [...rawData]
          .filter(x => x.weight && x.date)
          .sort((a, b) => b.date - a.date);
        if (sorted.length < 2) return null;
        latestValue = sorted[0].weight;
        previousValue = sorted[1].weight;
      } else if (type === 'waist') {
        sorted = [...rawData]
          .filter(x => x.waist && x.date)
          .sort((a, b) => b.date - a.date);
        if (sorted.length < 2) return null;
        latestValue = sorted[0].waist;
        previousValue = sorted[1].waist;
      }

      if (latestValue == null || previousValue == null) return null;

      const diff = latestValue - previousValue;
      const diffRounded = Math.round(Math.abs(diff) * 10) / 10;

      if (diff > 0) {
        return { direction: 'up', text: `↑ ${diffRounded}`, diff: diffRounded };
      } else if (diff < 0) {
        return { direction: 'down', text: `↓ ${diffRounded}`, diff: diffRounded };
      }
      return { direction: 'stable', text: '→ 0', diff: 0 };
    },

    calculateLabStatus(value, range) {
      // ✅ Nếu range là null hoặc "Đang cập nhật"
      if (!range || range.includes('null') || range === 'Đang cập nhật') {
        return { status: 'info', statusText: 'Đang cập nhật' };
      }

      try {
        // Parse value - loại bỏ ký tự không phải số
        const numValue = parseFloat(value.toString().replace(/[^\d.-]/g, ''));
        if (isNaN(numValue)) {
          return { status: 'info', statusText: 'Đang cập nhật' };
        }

        // Parse range (ví dụ: "70 - 100" hoặc "< 5.7" hoặc "> 100")
        const rangeStr = range.toString().trim();

        // Case 1: Range dạng "min - max" (vd: "70 - 100")
        const rangeMatch = rangeStr.match(/^([\d.]+)\s*[-–]\s*([\d.]+)$/);
        if (rangeMatch) {
          const min = parseFloat(rangeMatch[1]);
          const max = parseFloat(rangeMatch[2]);

          if (numValue < min) {
            return { status: 'warning', statusText: 'Thấp' };
          } else if (numValue >= min && numValue <= max) {
            return { status: 'success', statusText: 'Bình thường' };
          } else {
            return { status: 'danger', statusText: 'Cao' };
          }
        }

        // Case 2: Range dạng "< value" (vd: "< 5.7")
        const lessThanMatch = rangeStr.match(/^<\s*([\d.]+)$/);
        if (lessThanMatch) {
          const max = parseFloat(lessThanMatch[1]);
          if (numValue < max) {
            return { status: 'success', statusText: 'Bình thường' };
          } else {
            return { status: 'danger', statusText: 'Cao' };
          }
        }

        // Case 3: Range dạng "> value" (vd: "> 100")
        const greaterThanMatch = rangeStr.match(/^>\s*([\d.]+)$/);
        if (greaterThanMatch) {
          const min = parseFloat(greaterThanMatch[1]);
          if (numValue > min) {
            return { status: 'success', statusText: 'Bình thường' };
          } else {
            return { status: 'warning', statusText: 'Thấp' };
          }
        }

        // Case 4: Range dạng "<= value" (vd: "<= 5.7")
        const lessEqualMatch = rangeStr.match(/^<=?\s*([\d.]+)$/);
        if (lessEqualMatch) {
          const max = parseFloat(lessEqualMatch[1]);
          if (numValue <= max) {
            return { status: 'success', statusText: 'Bình thường' };
          } else {
            return { status: 'danger', statusText: 'Cao' };
          }
        }

        // Case 5: Range dạng ">= value" (vd: ">= 100")
        const greaterEqualMatch = rangeStr.match(/^>=?\s*([\d.]+)$/);
        if (greaterEqualMatch) {
          const min = parseFloat(greaterEqualMatch[1]);
          if (numValue >= min) {
            return { status: 'success', statusText: 'Bình thường' };
          } else {
            return { status: 'warning', statusText: 'Thấp' };
          }
        }

        return { status: 'info', statusText: 'Đang cập nhật' };

      } catch (error) {
        console.error('Error calculating lab status:', error);
        return { status: 'info', statusText: 'Đang cập nhật' };
      }
    },
    onMonthFilterChange(selectedOption) {
      this.labResultsFilters.monthFilter = selectedOption || null;
      this.filterLabResults();
    },

    onStatusFilterChange(selectedOption) {
      this.labResultsFilters.statusFilter = selectedOption || null;
      this.filterLabResults();
    },

    onCategoryFilterChange(selectedOption) {
      this.labResultsFilters.categoryFilter = selectedOption || null;
      this.filterLabResults();
    },
  }
};
</script>

<style scoped>
.chart-legend .legend-item {
  cursor: pointer !important;
  user-select: none;
  transition: all 0.2s ease;
  padding: 4px 8px;
  border-radius: 4px;
}

.chart-legend .legend-item:hover {
  background: #f3f4f6;
  transform: translateY(-1px);
}

.chart-legend .legend-item:active {
  transform: translateY(0);
}

/* ✅ Hidden legend state */
.chart-legend .legend-item.legend-hidden {
  opacity: 0.4;
  text-decoration: line-through;
}

.chart-legend .legend-item.legend-hidden .legend-dot {
  opacity: 0.3;
  filter: grayscale(100%);
}

/* Base Styles */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.patient-meta-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 14px;
  color: #6b7280;
  padding: 1rem 0;
}

.patient-name-lg {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin-right: 0.5rem;
}

.patient-basic-info,
.patient-meta,
.patient-next-visit {
  color: #6b7280;
  font-size: 14px;
}

.risk-badge {
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background-color: #fee2e2;
  color: #dc2626;
}

.patient-alert {
  display: inline-flex;
  align-items: center;
  color: #f59e0b;
  font-weight: 500;
  font-size: 14px;
}

.patient-alert svg {
  flex-shrink: 0;
}

.dashboard-container {
  width: 100%;
  background: #f8f9fa;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #1f2937;
}

.main-wrapper {
  max-width: 1600px;
  width: 100%;
  margin: 0 auto;
  padding: 1.5rem 2rem;
}

/* Page Header */
.page-header {
  margin-bottom: 1.5rem;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.25rem;
}

.page-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
}

/* Patient Info Card */
.patient-info-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.patient-info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.patient-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #10b981;
  font-weight: 600;
  font-size: 18px;
}

.patient-actions {
  background-repeat: no-repeat;
  box-sizing: inherit;
  margin-top: 5px;
  font: inherit;
  overflow: visible;
  text-transform: none;
  display: inline-block;
  font-weight: normal;
  text-align: center;
  user-select: none;
  padding: 0.65rem 1rem;
  font-size: 1rem;
  line-height: 1.5;

  box-shadow: none;
  margin-left: 0.75rem !important;
  outline: none !important;
  vertical-align: middle;
  text-decoration: none;
  color: #ffffff !important;
  background-color: #01645A !important;
  border: none !important;
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, -webkit-box-shadow 0.15s ease;
}

.btn-refresh,
.btn-search {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-refresh:hover,
.btn-search:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.btn-refresh:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #f3f4f6;
  color: #9ca3af;
}

.btn-refresh:disabled:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.text-muted {
  color: #9ca3af !important;
  font-style: italic;
}

.patient-details {
  display: grid;
  justify-content: space-between;
  align-items: center;
}



.patient-name-lg {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
}

.patient-phone {
  font-size: 0.875rem;
  color: #6b7280;
}

.patient-meta-info {
  margin-left: 10px;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  font-size: 16px;
}

.patient-meta {
  color: #6b7280;
}

.risk-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.risk-badge.high {
  background: #fef2f2;
  color: #dc2626;
}

.risk-badge.medium {
  background: #fef3c7;
  color: #d97706;
}

.risk-badge.low {
  background: #dcfce7;
  color: #16a34a;
}

.patient-alert {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: #d97706;
}

.patient-next-visit {
  color: #6b7280;
}

/* Tab Navigation */
.tab-navigation {
  display: flex;
  gap: 0.5rem;
  padding: 1rem 0;
  border-bottom: 1px solid #e5e7eb;
  width: 100%;
}

.tab-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  background-color: #ffffff;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  /* This makes each button take equal width */
  white-space: nowrap;
}

.tab-button:hover {
  background-color: #f9fafb;
  border-color: #d1d5db;
}

.tab-button.active {
  background-color: #01645A;
  color: #ffffff;
  border-color: #01645A;
}

.tab-button svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

/* For inactive buttons */
.tab-button:not(.active) svg {
  color: #6b7280;
}

/* For active button */
.tab-button.active svg {
  color: #ffffff;
}

/* Disabled tab */
.tab-button.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #f3f4f6;
}

.tab-button.disabled:hover {
  background-color: #f3f4f6;
  border-color: #e5e7eb;
}

.tab-lock-icon {
  font-size: 12px;
  margin-left: 4px;
}

/* Patient Tag Badges */
.patient-tag-badges {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
}

.tag-badges-group {
  display: inline-flex;
  gap: 4px;
}

.tag-badge {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.tag-badge.tag-diab {
  background-color: #01645a;
  color: white;
}

.tag-badge.tag-his {
  background-color: #ffa800;
  color: white;
}

/* Content Wrapper */
.content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Tabs Container - wrapper cho tất cả tab contents */
.tabs-container {
  display: flex;
  flex-direction: column;
  width: 100%;
}

/* Section Title Header */
.section-title-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.section-title-header h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
}

.last-updated {
  font-size: 0.875rem;
  color: #6b7280;
}

.section-title-with-search {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.search-input {
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  width: 250px;
}

.section-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.filter-select {
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;
}

.action-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn:hover {
  background: #f9fafb;
}

/* Metrics Grid */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.metric-card {
  background: white;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.metric-card.loading {
  opacity: 0.6;
  position: relative;
}

.metric-card.loading::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 20px;
  height: 20px;
  margin: -10px 0 0 -10px;
  border: 2px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.metric-card.disabled {
  opacity: 0.4;
  pointer-events: none;
  filter: grayscale(100%);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.metric-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.metric-label {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

.metric-value {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.value-number {
  font-size: 1.75rem;
  font-weight: 700;
  color: #111827;
}

.value-unit {
  font-size: 0.875rem;
  color: #6b7280;
}

.value-change {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
}

.value-change.up {
  background: #fef2f2;
  color: #dc2626;
}

.value-change.down {
  background: #dcfce7;
  color: #16a34a;
}

/* Metric Indicator Styles */
.metric-indicator {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 4px;
}

.metric-indicator.up {
  background: #fef2f2;
  color: #dc2626;
}

.metric-indicator.down {
  background: #dcfce7;
  color: #16a34a;
}

.metric-indicator.stable {
  background: #f3f4f6;
  color: #6b7280;
}

.metric-indicator svg {
  flex-shrink: 0;
}

.indicator-value {
  font-size: 0.7rem;
}

.metric-status {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  display: inline-block;
}

.metric-status.danger {
  background: #fef2f2;
  color: #dc2626;
}

.metric-status.warning {
  background: #fef3c7;
  color: #d97706;
}

.metric-status.success {
  background: #dcfce7;
  color: #16a34a;
}

/* Metric Indicator Styles (Tăng/Giảm) */
.metric-indicator {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 6px;
  vertical-align: middle;
}

.metric-indicator.up {
  background: #fef2f2;
  color: #dc2626;
}

.metric-indicator.down {
  background: #dcfce7;
  color: #16a34a;
}

.metric-indicator.stable {
  background: #f3f4f6;
  color: #6b7280;
}

.metric-indicator svg {
  flex-shrink: 0;
}

.metric-indicator .indicator-value {
  font-size: 0.7rem;
}

/* Stat Indicator Styles (cho Chart Stats) */
.stat-indicator {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 2px 5px;
  border-radius: 4px;
  margin-left: 4px;
  vertical-align: middle;
}

.stat-indicator.up {
  background: #fef2f2;
  color: #dc2626;
}

.stat-indicator.down {
  background: #dcfce7;
  color: #16a34a;
}

.stat-indicator.stable {
  background: #f3f4f6;
  color: #6b7280;
}

.stat-indicator svg {
  flex-shrink: 0;
}

/* Chart Section */
.chart-section {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Lab Results Section */
.lab-results-section {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.lab-results-section .section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.lab-results-section .section-header h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.lab-results-section .section-title-with-search {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.lab-results-section .section-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

/* Bottom Section Grid - Lab Results + Medications */
.bottom-section-grid {
  display: block;
  grid-template-columns: 1fr 380px;
  gap: 2rem;
  align-items: start;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.chart-title-group h3 {
  font-size: 1.125rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
}

.chart-legend {
  display: flex;
  gap: 1.5rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

/* Blood Pressure Chart */
.legend-dot.systolic {
  background: #ef4444;
}

.legend-dot.diastolic {
  background: #3b82f6;
}

.legend-dot.pulse {
  background: #10b981;
}

/* Glucose Chart */
.legend-dot.glucose-fasting {
  background: #3b82f6;
}

.legend-dot.before-meal {
  background: #10b981;
}

.legend-dot.after-meal {
  background: #8b5cf6;
}

.chart-controls {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
  /* ✅ Allow wrapping on small screens */
}

.chart-controls .basic-select {
  min-width: 120px;
  /* ✅ Ensure readable dropdown width */
}

.chart-action-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.chart-action-btn.danger {
  background: #fef2f2;
  color: #dc2626;
  border-color: #fecaca;
}

.chart-action-btn.success {
  background: #dcfce7;
  color: #16a34a;
  border-color: #bbf7d0;
}

.chart-action-btn:hover {
  opacity: 0.8;
}

.chart-body {
  position: relative;
}

.bp-chart-info {
  position: absolute;
  top: 20px;
  right: 20px;
  background: white;
  padding: 0.75rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
}

.bp-info-text {
  display: block;
  font-size: 0.875rem;
  font-weight: 700;
  color: #111827;
}

.bp-info-subtext {
  display: block;
  font-size: 0.75rem;
  color: #6b7280;
}

.chart-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.stat-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.5rem;
}

.stat-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.stat-value-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.stat-value {
  font-size: 1.125rem;
  font-weight: 700;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

/* Stat Indicator (for chart stats) */
.stat-indicator {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
}

.stat-indicator.up {
  background: #fef2f2;
  color: #dc2626;
}

.stat-indicator.down {
  background: #dcfce7;
  color: #16a34a;
}

.stat-indicator.stable {
  background: #f3f4f6;
  color: #6b7280;
}

.stat-indicator svg {
  flex-shrink: 0;
}

.stat-date {
  font-size: 0.75rem;
  color: #6b7280;
}

.stat-date.up {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: #dc2626;
  font-weight: 600;
}

.chart-no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: #9ca3af;
}

.chart-no-data p {
  margin-top: 1rem;
  font-size: 0.875rem;
}

/* Lab Results */
.lab-results-wrapper {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.lab-results-table {
  width: 100%;
  border-collapse: collapse;
}

.lab-results-table thead {
  background: #01645A;
}

.lab-results-table th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  color: #ffffff;
  text-transform: uppercase;
  border-bottom: 1px solid #01645A;
}

.lab-results-table td {
  padding: 1rem;
  font-size: 0.875rem;
  color: #374151;
  border-bottom: 1px solid #f3f4f6;
}

.value-cell {
  font-weight: 600;
}

.value-cell.danger {
  color: #dc2626;
}

.value-cell.warning {
  color: #d97706;
}

/* Status Badge Styles - Simple Version */
.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  display: inline-block;
  text-align: center;
  min-width: 90px;
  transition: all 0.2s ease;
  letter-spacing: 0.3px;
}

/* Danger - Cao bất thường / Cao */
.status-badge.danger {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #991b1b;
  border: 1px solid #fca5a5;
  box-shadow: 0 1px 3px rgba(220, 38, 38, 0.2);
}

/* Warning - Thấp */
.status-badge.warning {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #92400e;
  border: 1px solid #fcd34d;
  box-shadow: 0 1px 3px rgba(217, 119, 6, 0.2);
}

/* Success - Bình thường */
.status-badge.success {
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
  color: #14532d;
  border: 1px solid #86efac;
  box-shadow: 0 1px 3px rgba(22, 163, 74, 0.2);
}

/* Info - Đang cập nhật */
.status-badge.info {
  background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
  color: #0c4a6e;
  border: 1px solid #7dd3fc;
  box-shadow: 0 1px 3px rgba(3, 105, 161, 0.2);
}

/* Hover effects */
.status-badge:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.status-badge.danger:hover {
  box-shadow: 0 4px 8px rgba(220, 38, 38, 0.3);
}

.status-badge.warning:hover {
  box-shadow: 0 4px 8px rgba(217, 119, 6, 0.3);
}

.status-badge.success:hover {
  box-shadow: 0 4px 8px rgba(22, 163, 74, 0.3);
}

.status-badge.info:hover {
  box-shadow: 0 4px 8px rgba(3, 105, 161, 0.3);
}

/* Value Cell Styles */
.value-cell {
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  position: relative;
}

.value-cell.danger {
  color: #dc2626;
  font-weight: 700;
}

.value-cell.warning {
  color: #d97706;
  font-weight: 700;
}

.value-cell.success {
  color: #16a34a;
  font-weight: 600;
}

.value-cell.info {
  color: #0369a1;
  font-weight: 600;
}

/* Enhanced Table Row Hover */
.lab-results-table tbody tr {
  transition: all 0.2s ease;
}

.lab-results-table tbody tr:hover {
  background-color: #f9fafb;
  transform: translateX(2px);
}

.lab-results-table tbody tr:hover .status-badge {
  transform: translateY(-2px) scale(1.05);
}

/* Pagination Controls */
.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pagination-btn svg {
  width: 16px;
  height: 16px;
}

.page-info {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
  min-width: 60px;
  text-align: center;
}

/* Responsive */
@media (max-width: 768px) {
  .status-badge {
    font-size: 0.7rem;
    padding: 0.2rem 0.6rem;
    min-width: 75px;
  }

  .value-cell {
    font-size: 0.8rem;
  }
}

/* Dark mode support (optional) */
@media (prefers-color-scheme: dark) {
  .status-badge.danger {
    background: linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%);
    color: #fecaca;
    border-color: #991b1b;
  }

  .status-badge.warning {
    background: linear-gradient(135deg, #78350f 0%, #92400e 100%);
    color: #fde68a;
    border-color: #92400e;
  }

  .status-badge.success {
    background: linear-gradient(135deg, #14532d 0%, #166534 100%);
    color: #bbf7d0;
    border-color: #166534;
  }

  .status-badge.info {
    background: linear-gradient(135deg, #0c4a6e 0%, #075985 100%);
    color: #bae6fd;
    border-color: #075985;
  }
}

.results-note {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
  font-size: 0.875rem;
  color: #6b7280;
}

/* Medications Sidebar */
.medications-sidebar {
  margin-top: 25px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

.sidebar-section {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.sidebar-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.sidebar-header h3 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
}

.medication-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.medication-item {
  padding: 1rem;
  background: #eff6ff;
  border-radius: 8px;
  border-left: 3px solid #3b82f6;
}

.medication-item h4 {
  font-size: 0.875rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
}

.medication-item p {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0.25rem 0;
}

.recommendations-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.recommendation-box {
  padding: 1rem;
  background: #fef3c7;
  border-radius: 8px;
  border-left: 3px solid #d97706;
}

.recommendation-box h4 {
  font-size: 0.875rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.75rem;
}

.recommendation-box ul {
  list-style: disc;
  padding-left: 1.25rem;
  margin-bottom: 1rem;
}

.recommendation-box li {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 0.5rem;
}

.recommendation-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.action-link {
  padding: 0.375rem 0.75rem;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.75rem;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
}

.action-link:hover {
  background: #f9fafb;
}

.action-link-sm {
  font-size: 0.75rem;
  color: #6b7280;
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
}

.btn-close {
  background-repeat: no-repeat;
  box-sizing: inherit;
  margin-top: 5px;
  font: inherit;
  overflow: visible;
  text-transform: none;
  display: inline-block;
  font-weight: normal;
  text-align: center;
  user-select: none;
  padding: 0.65rem 1rem;
  font-size: 1rem;
  line-height: 1.5;
  border-radius: 0.42rem;
  box-shadow: none;
  margin-left: 0.75rem !important;
  outline: none !important;
  vertical-align: middle;
  text-decoration: none;
  color: #888C9F !important;
  background-color: #F3F6F9 !important;
  border: none !important;
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, -webkit-box-shadow 0.15s ease;
}

.medication-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 600px;
  overflow-y: auto;
}

.recommendations-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 600px;
  overflow-y: auto;
}

.medication-item {
  padding: 16px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.medication-item:hover {
  font-size: 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.medication-item h4 {
  margin: 0 0 12px 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: #1f2937;
}

.medication-item p {
  margin: 6px 0;
  font-size: 0.75rem;
  color: #6b7280;
}

.medication-item p strong {
  color: #374151;
  font-weight: 500;
}

.medication-item.expiring-soon {
  border-left: 4px solid #f59e0b;
  background: #fffbeb;
}

.expiry-warning {
  margin-top: 12px;
  padding: 8px 12px;
  background: #fef3c7;
  border-radius: 6px;
  color: #92400e;
  font-size: 0.75rem;
  font-weight: 500;
}

.instructions {
  margin-top: 12px;
  padding: 8px 12px;
  background: #f3f4f6;
  border-radius: 6px;
  font-style: italic;
}

.no-data-message {
  padding: 40px 20px;
  text-align: center;
  color: #9ca3af;
  font-style: italic;
  font-size: 14px;
}

/* Responsive */
@media (max-width: 1200px) {
  .bottom-section-grid {
    grid-template-columns: 1fr;
  }

  .metrics-grid {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  }
}

@media (max-width: 768px) {
  .main-wrapper {
    padding: 1rem;
  }

  .patient-details {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .patient-meta-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .tab-navigation {
    overflow-x: auto;
  }

  .metrics-grid {
    grid-template-columns: 1fr;
  }

  .chart-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .chart-stats {
    grid-template-columns: 1fr;
  }

  .section-title-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .section-title-with-search {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }

  .search-input {
    width: 100%;
  }

  .medications-sidebar {
    grid-template-columns: 1fr;
  }
}

/* Tab Content Sections */
.tab-content-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6rem 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  text-align: center;
  min-height: 400px;
}

.empty-state svg {
  margin-bottom: 1.5rem;
  opacity: 0.5;
}

.empty-state h3 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
}

.empty-state p {
  font-size: 0.875rem;
  color: #6b7280;
}

/* Utility Classes */
.mt-4 {
  margin-top: 2rem;
}

.metric-date {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f3f4f6;
  font-size: 0.75rem;
  color: #6b7280;
  font-style: italic;
  text-align: center;
}

.chart-legend .legend-item {
  transition: all 0.2s ease;
}

.chart-legend .legend-item:hover {
  opacity: 0.7;
  transform: scale(1.05);
}

/* ✅ Hidden legend item style */
.chart-legend .legend-item.legend-hidden {
  opacity: 0.3;
  text-decoration: line-through;
}

.chart-legend .legend-item.legend-hidden .legend-dot {
  opacity: 0.3;
}

/* ✅ Helper text style */
.chart-helper-text {
  margin-top: 12px;
  padding: 10px 14px;
  background: #f0f9ff;
  border-left: 3px solid #3b82f6;
  border-radius: 6px;
  font-size: 12px;
  color: #1e40af;
  line-height: 1.5;
}

.chart-helper-text strong {
  font-weight: 600;
  color: #1e3a8a;
}

/* ✅ Make chart markers look more clickable */
.apexcharts-marker {
  cursor: pointer !important;
  transition: all 0.2s ease;
}

.apexcharts-marker:hover {
  transform: scale(1.3);
  filter: brightness(1.2);
}

/* ✅ Active marker state */
.apexcharts-marker:active {
  transform: scale(1.1);
  filter: brightness(0.9);
}

/* ✅ Chart legend clickable cursor */
.chart-legend .legend-item[style*="cursor: pointer"] {
  user-select: none;
}

.status-badge.info {
  background: #e0f2fe;
  color: #0369a1;
}

/* ✅ Responsive helper text */
@media (max-width: 768px) {
  .chart-helper-text {
    font-size: 11px;
    padding: 8px 12px;
  }
}
</style>